import type { GenreAvatar } from "@maxime/shared";

import type { PoseHeros } from "./heros";

/**
 * ⟦C15 · A-135 · A-136⟧ **Quel fond de chambre afficher.**
 *
 * Les 10 planches `assets/img-src/chambre/chambre-n1..n5-{jour,nuit}.png` remplaçaient les deux
 * fonds R1/R2 du reskin 8-bit (⟦A-134⟧) par CINQ niveaux : `state.milestones_reached` (aucun
 * nouveau flag, CLAUDE.md : l'état neutre par défaut) faisait basculer la chambre de n1 à n2
 * (100), n3 (10 000), n4 (100 000, « le studio ») puis n5 (1 000 000, la consécration).
 *
 * ⟦C55 · A-172, décision de Matt du 01/10/2026⟧ **La chambre reste `chambre-n1` du début à la
 * fin.** Capture de Matt à l'appui : dès 100 abonnés le jeu bascule sur `chambre-n2`, une AUTRE
 * pièce d'un autre style, qui n'a reçu aucun des chantiers C29 à C41 (calques, zones cliquables,
 * mur, poses) et où les objets de palier flottaient en emoji sans place dans le dessin. Choix
 * proposé et retenu : « n1 + petits ajouts dessinés » : la chambre ne change plus jamais de
 * planche, et chaque objet de palier (`DECORS_PALIER`, `Chambre.tsx`) est désormais un CALQUE
 * pixel art posé DANS le dessin de n1 (`calqueDecoChambreN1`, plus bas). `niveauChambre` garde sa
 * signature et son type (`NiveauChambre`) mais ne lit plus l'historique de paliers : elle rend
 * toujours 1. Les planches `chambre-n2..n5-{jour,nuit}.png` restent sur le disque (jamais
 * supprimer un asset), plus aucun chemin du jeu n'y mène, pas même l'écran-titre : son fond
 * (`FondMenu.tsx`) ne montre plus que le fond de n1 sans genre (⟦C55 · A-172⟧, plus de diaporama).
 */

export type NiveauChambre = 1 | 2 | 3 | 4 | 5;

/** Le niveau de chambre pour l'historique de paliers franchis d'une run : TOUJOURS 1 depuis
 *  ⟦C55 · A-172⟧ (voir le commentaire de tête). Le paramètre est gardé (signature inchangée pour
 *  `Chambre.tsx` et les tests) mais n'est plus lu : l'historique ne pilote plus que les objets
 *  de palier (`DECORS_PALIER`), jamais la planche. */
export function niveauChambre(_paliersFranchis: readonly number[]): NiveauChambre {
  return 1;
}

/** La clé de scène — « chambre-n3-nuit » — sert à la fois au chemin de la planche et à la
    table de hotspots ci-dessous : une seule fonction qui les construit toutes les deux, pour
    ne jamais les faire dériver l'une de l'autre. */
export function nomScene(niveau: NiveauChambre, nuit: boolean): string {
  return `chambre-n${niveau}-${nuit ? "nuit" : "jour"}`;
}

/** Le chemin de la planche (`client/public/scenes/chambre-nX-{jour,nuit}.png`).
 *
 * ⟦C34 · A-159⟧ **`chambre-n1` n'a plus de fichier « base » propre.** Depuis ce chantier, n1 se
 * rend en calques (`fondChambreN1`/`calqueLiegeChambreN1`/`calqueTendancesChambreN1`/
 * `calquePoseChambreN1`, plus bas) : un fond FIXE (liège vide, les deux cadres photo d'origine)
 * plus des calques posés dessus au déblocage. `sceneChambre(1, …)` ne sert donc plus qu'au repli
 * SANS GENRE (parties d'avant ⟦A-4 §2⟧, ou le fond de l'écran-titre `FondMenu.tsx` qui n'a pas de genre) :
 * il retombe sur `fondChambreN1(null)`, exactement le fond que `Chambre.tsx` compose pour ce
 * cas-là. ⟦C33 · A-158⟧ Le paramètre `liegeVide` disparaît avec l'ancien fichier jumeau
 * `-liege-vide.png` (le liège vide est maintenant l'état PAR DÉFAUT du fond, plus un fichier à
 * choisir) : tout appelant qui le passait encore doit lire `ouvert(state, "arbre")` pour poser
 * le calque `calqueLiegeChambreN1`, comme le fait désormais `Chambre.tsx`. */
export function sceneChambre(niveau: NiveauChambre, nuit: boolean): string {
  if (niveau === 1) return fondChambreN1(null);
  return `/scenes/${nomScene(niveau, nuit)}.png`;
}

/**
 * ⟦Consigne de Mathieu, 29/08⟧ **Les zones cliquables suivent le dessin, jamais l'inverse.**
 *
 * « Les objets ne sont pas au même endroit d'une image à l'autre — FLUX a composé chaque
 * pièce différemment. » Vérifié à l'écran : les 9 planches distinctes (n1-jour et n1-nuit sont
 * un fichier identique, cf. l'avenant de constat) ont chacune leur propre composition —
 * certaines n'ont même pas les deux meubles qu'on cherche. Relevé À LA MAIN sur chaque planche
 * (grille de repérage en %, jamais deviné) :
 *
 * | scène | lit | bureau | fenêtre | étagère |
 * |---|---|---|---|---|
 * | n1 (jour = nuit) | ✓ | ✓ (défaut A-139 §1 réparé ⟦C21⟧, voir plus bas) | ✓ | — |
 * | n2-jour | ✓ | — aucun bureau dessiné | — | ✓ (étagère murale) |
 * | n2-nuit | ✓ | — aucun bureau dessiné | ✓ | — |
 * | n3-jour | ✓ | ✓ | ✓ | ✓ |
 * | n3-nuit | ✓ | ✓ | ✓ | ✓ |
 * | n4-jour | — pas de lit (un canapé, pas un lit) | ✓ | ✓ | — (armoire, pas une étagère) |
 * | n4-nuit | — idem | ✓ | ✓ | ✓ |
 * | n5-jour | — le plateau n'a ni lit ni bureau | — | ✓ | ✓ |
 * | n5-nuit | — idem | — | ✓ | ✓ |
 *
 * Un id absent de la ligne d'une scène ⇒ `hotspotChambre` renvoie `position: null` : jamais un
 * hotspot invisible posé au hasard sur un autre meuble (c'est exactement le bug corrigé une
 * première fois en ⟦A-134⟧ — « le bureau atterrissait sur le matelas »). `Chambre.tsx` retombe
 * alors sur la position par défaut de `Studio.tsx` **et affiche l'emoji de repli** : pour
 * `bureau`/`lit` (les deux seuls objets normalement invisibles, « peints dans le fond »), un
 * pion visible plutôt qu'un carré vide dit honnêtement « clique ici, mais ce n'est pas un
 * meuble précis de cette pièce ».
 *
 * ⟦C26⟧ **`chambre-n1-jour` change de planche de base dans la nuit du 12/09** (mauvais
 * agencement de l'ancienne, régénérée par Matt) : le nouveau candidat Higgsfield 32
 * (`hf-v3-32-tete-gauche.png`) devient la planche de base — relevé À NOUVEAU sur une grille de
 * repérage en %, jamais deviné (`scratchpad/pipeline_c26.py` + grilles de vérification, hors
 * dépôt). Nouvelle composition : lit contre le mur du fond gauche, panneau à post-it (« tableau »
 * dans le code, aucun changement d'id) et deux cadres au-dessus, commode + fenêtre sur le mur du
 * fond droit, bureau et chaise contre le bord ouvert de droite, tapis au centre. Quatre objets
 * cliquables seulement, comme avant ce chantier — la commode et le tapis (support du futur objet
 * `telephone`) sont de nouveaux éléments dessinés mais **sans hotspot dédié pour l'instant**
 * (décision de Mathieu en conversation, 13/09 : « ne pose un hotspot que pour lit, bureau,
 * fenêtre, panneau ») : `telephone` garde le défaut de `Studio.tsx` s'il se déverrouille sur ce
 * niveau, exactement comme avant ce chantier, et `commode` n'est toujours l'id d'aucun objet
 * jouable. Points relevés (grille en %, zoomés à l'œil) : lit → pile sur l'oreiller ; bureau →
 * sur l'écran du moniteur (le point le plus stable du meuble, jamais la chaise) ; fenêtre → au
 * centre du carreau ; tableau → au centre du panneau à post-it. `chambre-n1-nuit` reste le MÊME
 * fichier PNG que `chambre-n1-jour` (`md5` identique, vérifié) : même table pour les deux.
 *
 * ⟦C40 · A-163⟧ **`tendances` rejoint la table.** Retour de Matt (18/09) : « il y a le petit logo
 * qui s'est rajouté [sur le tableau des tendances], faut pas. » Cause : `tendances` n'avait AUCUNE
 * entrée ici, donc `hotspotChambre` renvoyait `position: null` et affichait l'emoji de repli
 * (`REPLI_EMOJI`, plus bas) PAR-DESSUS le tableau peint — exactement le défaut déjà corrigé pour
 * `fenetre`/`tableau` en ⟦C29⟧. Position = le centre de l'emprise déjà mesurée du tableau des
 * tendances (`EMPRISE_PAR_SCENE.tendances`, plus bas dans ce fichier, elle-même reprise du
 * rectangle de collage RÉEL `FRAMES_RECT` de `scripts/mur-veille-branchement.py`) : jamais un
 * point deviné à part, un SEUL rectangle mesuré nourrit à la fois le hotspot et l'emprise
 * agrandie, aucun risque de divergence entre les deux. Centre : x=(34.6+43.4)/2=39.0,
 * y=(19.1+38.7)/2=28.9 — et c'est bien la valeur (39, 29) déjà posée en repli par défaut dans
 * `Studio.tsx` (`objets.push({ id: "tendances", x: 39, y: 29, … })`) avant ce chantier : les deux
 * s'accordaient déjà, il ne manquait que l'entrée ici pour que `hotspotChambre` la voie.
 */
const HOTSPOTS_PAR_SCENE: Readonly<Record<string, Readonly<Record<string, { x: number; y: number }>>>> = {
  "chambre-n1-jour": { lit: { x: 27, y: 58 }, bureau: { x: 77, y: 55 }, fenetre: { x: 68, y: 25 }, tableau: { x: 27, y: 35 }, tendances: { x: 39.0, y: 28.9 } },
  "chambre-n1-nuit": { lit: { x: 27, y: 58 }, bureau: { x: 77, y: 55 }, fenetre: { x: 68, y: 25 }, tableau: { x: 27, y: 35 }, tendances: { x: 39.0, y: 28.9 } },
  "chambre-n2-jour": { lit: { x: 55, y: 68 }, etagere: { x: 56, y: 39 } },
  "chambre-n2-nuit": { lit: { x: 54, y: 70 }, fenetre: { x: 26, y: 24 } },
  "chambre-n3-jour": { bureau: { x: 53, y: 49 }, lit: { x: 35, y: 58 }, fenetre: { x: 70, y: 28 }, etagere: { x: 47, y: 28 } },
  "chambre-n3-nuit": { bureau: { x: 54, y: 49 }, lit: { x: 36, y: 61 }, fenetre: { x: 70, y: 26 }, etagere: { x: 46, y: 30 } },
  "chambre-n4-jour": { bureau: { x: 43, y: 53 }, fenetre: { x: 26, y: 26 } },
  "chambre-n4-nuit": { bureau: { x: 45, y: 54 }, fenetre: { x: 25, y: 26 }, etagere: { x: 66, y: 33 } },
  "chambre-n5-jour": { fenetre: { x: 19, y: 35 }, etagere: { x: 41, y: 48 } },
  "chambre-n5-nuit": { fenetre: { x: 22, y: 23 }, etagere: { x: 37, y: 41 } },
};

/** L'emoji de repli, pour les objets normalement invisibles parce que le dessin les porte déjà
    ⟦A-137⟧. Un id qui a toujours porté son propre emoji (étagère…) garde le sien : `Chambre.tsx`
    ne touche à l'emoji QUE quand `repli` n'est pas `null` ici.
    ⟦C29 · lot A · point 2⟧ `fenetre` et `tableau` rejoignent `bureau`/`lit` : les quatre sont
    peints dans `chambre-n1` (et `fenetre` sur les cinq niveaux), poser leur emoji par-dessus
    doublait le dessin (constat de Mathieu, 14/09 : « le vieil icône sur la fenêtre »). Le repli
    ne sert donc plus qu'aux scènes qui NE peignent PAS l'objet : `fenetre` a une entrée dans
    `HOTSPOTS_PAR_SCENE` pour les cinq niveaux (le repli ne s'y déclenche jamais aujourd'hui,
    gardé par défense si une planche future en manquait) ; `tableau` (le panneau de liège,
    ⟦C33 · A-158⟧) et `tendances` (le tableau des tendances, même chantier) n'ont d'entrée que
    sur `chambre-n1` — leur repli s'appliquait donc sur n2-n5, dès que ces niveaux ouvraient
    `arbre`/`tendances`, jamais un hotspot invisible posé au hasard.
    ⟦C55 · A-172⟧ **La scène de jeu est TOUJOURS `chambre-n1`** (`niveauChambre`, plus haut) : ces
    cinq objets y ont tous leur position dessinée (`HOTSPOTS_PAR_SCENE`), le repli ne se déclenche
    donc plus jamais en jeu, à aucun palier (verrouillé par `chambre.test.ts`). La table reste, par
    défense, pour la scène hypothétique d'une planche qui ne peindrait pas l'un d'eux. */
const REPLI_EMOJI: Readonly<Record<string, string>> = {
  bureau: "🖥️",
  lit: "🛏️",
  fenetre: "🪟",
  tableau: "📌",
  tendances: "📈",
};

export interface HotspotResolu {
  /** `null` ⇒ aucune position dédiée pour cette scène : `Chambre.tsx` garde le défaut de
      `Studio.tsx`. */
  position: { x: number; y: number } | null;
  /** Non-`null` ⇒ afficher CET emoji par-dessus le défaut (repli visible, jamais un hotspot
      invisible mal placé). */
  repli: string | null;
}

export function hotspotChambre(scene: string, id: string): HotspotResolu {
  const position = HOTSPOTS_PAR_SCENE[scene]?.[id] ?? null;
  return { position, repli: position === null ? (REPLI_EMOJI[id] ?? null) : null };
}

/**
 * ⟦C21 bis · point 1⟧ **Dimensions natives des dix planches** (toutes 1024×576 — commentaire de
 * `.chambre-scene`, `app.css` : « un seul ratio, plus besoin d'une règle par variante »).
 */
export const LARGEUR_IMAGE_CHAMBRE = 1024;
export const HAUTEUR_IMAGE_CHAMBRE = 576;

/**
 * ⟦C21 bis · point 1⟧ **La scène plein cadre, sans bande** (ordinateur), ⟦C21 ter · point 1⟧
 * **généralisée à deux modes**, `cover` (ordinateur, inchangé) et `contain` (mobile, nouveau).
 *
 * Avant C21 bis, `.chambre-scene` forçait `aspect-ratio: 1024/576` : la boîte affichée avait
 * TOUJOURS exactement le ratio de l'image, donc une position en % de l'image valait aussi une
 * position en % de la boîte (sans quoi ⟦A-137⟧ n'aurait jamais tenu). Mais forcer ce ratio, sur
 * une boîte que la mise en page ne contraint pas à ce ratio (le cadre `.zone-scene` en U du
 * bureau), laissait le fond bleu marine de `.zone-scene` déborder autour (les « bandes
 * sombres » remontées par les relecteurs de C21 bis). Corrigé pour l'ordinateur : `mode:
 * "cover"` remplit TOUJOURS 100 % de la boîte, quitte à recadrer (centré) l'image.
 *
 * Sur mobile, `cover` recadrait au contraire TROP : une boîte très haute et étroite (portrait)
 * forçait une échelle énorme (recadrage par la hauteur) et ne laissait voir qu'une bande
 * verticale de la pièce, constat C21 ter : `.objet-bureau .objet-nom` rogné par le bord droit.
 * La maquette F3 montre la pièce ENTIÈRE sur mobile : `mode: "contain"` met l'image à l'échelle
 * par le PLUS PETIT facteur (jamais de recadrage), ancrée en HAUT (jamais centrée verticalement,
 * « l'image ancrée en haut sous l'en-tête » du chantier) mais toujours centrée horizontalement
 * (`decalageX` inchangé entre les deux modes : sur les planches 1024×576 posées dans une boîte
 * mobile portrait, `contain` cale l'échelle sur la LARGEUR, donc `decalageX` vaut déjà 0, « contain
 * sur la largeur » du chantier n'est pas un troisième mode, c'est ce que `contain` fait déjà ici).
 * L'espace qui reste sous l'image (elle ne remplit plus toute la hauteur) est comblé par
 * `background-color` = la couleur de coin de la planche (`couleurCoinChambre`, plus bas) posée
 * par `Chambre.tsx`, jamais par du vide tramé.
 *
 * Fonction pure (aucun DOM, aucune fenêtre) : `boite.largeur`/`boite.hauteur` sont déjà mesurés
 * par l'appelant. Quand la boîte a EXACTEMENT le ratio de l'image (aucun recadrage), les deux
 * modes se réduisent à l'identité, c'est ce que vérifie `chambre.test.ts`.
 */
/**
 * ⟦C21 ter · point 4⟧ **Un hotspot ne sort jamais du cadre.** `positionDansBoite` pouvait
 * renvoyer un point hors de [0, 100] sans le signaler (légitime en mode `cover`, le recadrage
 * pousse littéralement certains points hors champ, cf. `chambre.test.ts`), mais rien n'avertit
 * si une planche future recadre plus que prévu et fait sortir un meuble entier de l'écran.
 * Clampe dans [0, 100] et journalise en dev (`console.warn`, éliminé du bundle de production
 * comme tout ce qui dépend d'`import.meta.env.DEV`, cf. `?devScore` de l'Atelier), jamais en production,
 * jamais un avertissement qui casse un test qui ne le vise pas explicitement.
 */
function clampePourcent(p: { x: number; y: number }): { x: number; y: number } {
  const x = Math.min(100, Math.max(0, p.x));
  const y = Math.min(100, Math.max(0, p.y));
  if (import.meta.env.DEV && (x !== p.x || y !== p.y)) {
    console.warn(
      `positionDansBoite: hotspot hors cadre (${p.x.toFixed(1)}, ${p.y.toFixed(1)}) ramené à (${x.toFixed(1)}, ${y.toFixed(1)})`,
    );
  }
  return { x, y };
}

/**
 * ⟦C21 quater · point 2⟧ **Un troisième mode : l'échelle EXPLICITE.** `"cover"` et `"contain"`
 * calculent tous deux leur échelle à partir du ratio boîte/image (`Math.max`/`Math.min`), mais
 * sur mobile, `contain` cale la pièce sur la largeur EXACTE de la boîte (aucune marge), et la
 * planche a ses propres marges bleues sur les côtés (constat du chantier) : on peut zoomer un
 * peu sans rogner la pièce. Un `mode` numérique (ex. `1.35`) multiplie l'échelle « cale sur la
 * largeur » (`boite.largeur / LARGEUR_IMAGE_CHAMBRE`) par ce facteur, un simple agrandissement,
 * jamais un troisième calcul de recadrage. `decalageX` (toujours centré, cf. plus bas) rogne
 * alors symétriquement les marges qui dépassent de chaque côté, `decalageY` reste nul comme en
 * `contain` (ancré en haut, jamais centré verticalement).
 */
export type AjustementScene = "cover" | "contain" | number;

/**
 * ⟦C21 quater · point 1⟧ **Le rectangle RÉELLEMENT affiché de l'image, en pixels**, factorisé
 * hors de `positionDansBoite` pour que `Chambre.tsx` puisse aussi caler la TAILLE de l'avatar
 * dessus (`--taille-heros`), pas seulement la position des hotspots : les deux doivent suivre
 * le même rectangle, jamais deux calculs qui pourraient diverger. Repli sur le ratio natif de
 * l'image tant que la boîte n'est pas mesurée (largeur/hauteur ≤ 0), identique au repli déjà
 * fait par `positionDansBoite` avant ce chantier.
 */
export function rectangleImageAffichee(
  boite: { largeur: number; hauteur: number },
  mode: AjustementScene = "cover",
): { largeur: number; hauteur: number } {
  if (boite.largeur <= 0 || boite.hauteur <= 0) {
    return { largeur: LARGEUR_IMAGE_CHAMBRE, hauteur: HAUTEUR_IMAGE_CHAMBRE };
  }
  const echelle =
    typeof mode === "number"
      ? (boite.largeur / LARGEUR_IMAGE_CHAMBRE) * mode
      : mode === "cover"
        ? Math.max(boite.largeur / LARGEUR_IMAGE_CHAMBRE, boite.hauteur / HAUTEUR_IMAGE_CHAMBRE)
        : Math.min(boite.largeur / LARGEUR_IMAGE_CHAMBRE, boite.hauteur / HAUTEUR_IMAGE_CHAMBRE);
  return { largeur: LARGEUR_IMAGE_CHAMBRE * echelle, hauteur: HAUTEUR_IMAGE_CHAMBRE * echelle };
}

/**
 * ⟦C26 bis · point 3⟧ **Un second ancrage vertical, réservé aux modes non-`cover`.** Jusqu'ici
 * `contain`/l'échelle explicite ancraient TOUJOURS l'image en haut de la boîte (⟦C21 ter⟧,
 * verrouillé par `chambre.test.ts`) : correct tant que la boîte mobile a à peu près le ratio de
 * l'image, faux dès qu'elle est beaucoup plus haute qu'elle ne l'est large : le cas du
 * téléphone en portrait. Constat du chantier (`apres-c26-390-05-passer.png`) : sous 768 px, la
 * pièce occupe le haut de `.scene-mobile-zone` puis ~210 px CSS de bleu vide (la couleur de
 * coin, `couleurCoinChambre`) avant la boîte de Matt (la boîte flottante ne pousse jamais la
 * scène, elle se pose PAR-DESSUS, `Studio.tsx`), donc le vide ne se voit jamais résorbé tout
 * seul par la mise en page.
 *
 * Deux corrections possibles (le chantier laisse le choix, « celle qui laisse la pièce la plus
 * grande ») : agrandir encore l'échelle mobile (`echellePieceMobile`), ou ancrer la scène en
 * BAS plutôt qu'en haut (le vide remonte sous l'en-tête, où il ne sépare plus la pièce de la
 * boîte). La première option est déjà à son maximum pour `chambre-n1` : `echellePieceMobile`
 * cale la LARGEUR à 8 px des bords (⟦A-137⟧-like, mesuré dans son propre commentaire) :
 * l'agrandir encore mangerait cette marge avant de combler un vide qui est d'abord un problème
 * de HAUTEUR (l'image, 1024×576, est structurellement plus large que haute ; une boîte mobile
 * portrait ne peut pas caler les deux dimensions à la fois sans recadrer, ce que `contain`
 * interdit par construction). L'ancrage bas est donc retenu : il ne change RIEN à la largeur ni
 * à l'échelle, seulement à quelle moitié de la boîte porte le vide.
 *
 * Paramètre séparé plutôt qu'un changement du comportement par défaut : `chambre.test.ts`
 * verrouillait explicitement « ancrée en haut, jamais centrée verticalement » comme un choix de
 * ⟦C21 ter · C21 quater⟧, et `Chambre.tsx` a deux instances (`cover` ordinateur, `contain`
 * mobile) : changer le défaut aurait fallu re-décider pour l'ordinateur aussi, hors sujet ici.
 * `"haut"` (répli, tout appel existant inchangé) garde l'ancien calcul au bit près ; `"bas"` est
 * la même formule que `"haut"`, sans le partage par deux qui centre `cover` : l'image commence à
 * `boite.hauteur - hauteurAffichee` au lieu de 0, son bord BAS touche donc le bord bas de la
 * boîte plutôt que son bord haut. `mode === "cover"` reste seul maître de son propre centrage
 * (recadrage symétrique, ce paramètre n'y change rien, comme documenté plus haut).
 *
 * `reserveBasPx` (repli 0) affine encore l'ancrage `"bas"` : trouvé en vérifiant le point 1 sur
 * cette même scène, la plaque du bureau, posée sous le personnage (bas de l'image), tombait
 * TOUJOURS derrière la barre d'onglets mobile (`.onglets`, 62 px, TOUJOURS visible, contrairement
 * à la boîte de Matt ou au panneau) une fois la scène ancrée bord à bord avec `.poste`. Relu le
 * chantier au mot près : « la pièce descend jusqu'AU-DESSUS de la boîte », pas derrière. Un
 * décalage supplémentaire au bord bas (en pixels, l'épaisseur d'un élément d'habillage fixe,
 * jamais un pourcentage d'image) réserve cette bande : l'image s'arrête juste au-dessus, au lieu
 * de continuer dessous sans que personne ne la voie. `Chambre.tsx` passe la hauteur de
 * `.onglets` (`--tabs-h`, `app.css`) ; ce module reste pur et ignore tout de la mise en page,
 * il ne fait qu'appliquer le nombre de pixels qu'on lui donne.
 */
/**
 * ⟦C26 ter · point 1⟧ **Un troisième ancrage, `"centre"`.** L'ancrage `"bas"` déplaçait le vide
 * résiduel sous la pièce (mobile, l'image ne remplit jamais la hauteur d'une boîte portrait) vers
 * le HAUT de la boîte, entre l'en-tête et la pièce : correct tant que quelque chose occupe le bas
 * de l'écran (la fenêtre Actions, ⟦C21 §1.4⟧, ou la boîte de Matt), faux dès que ce n'est plus le
 * cas (la fenêtre repliée, « la chambre respire ») : la pièce restait collée en bas, tout le vide
 * remontait alors d'un bloc au-dessus d'elle (constat du chantier, `apres-c26bis-390-f-01-
 * repos.png`). `"centre"` répartit le vide symétriquement, la même formule de centrage que
 * `"cover"` (`(hauteurAffichee - boite.hauteur) / 2`), mais pour les modes non-`cover` :
 * `reserveBasPx` ne le concerne pas (un vide symétrique n'a pas de bord à réserver), comme il ne
 * concernait déjà pas `"haut"`.
 */
export type AncrageVertical = "haut" | "bas" | "centre";

export function positionDansBoite(
  pourcentImage: { x: number; y: number },
  boite: { largeur: number; hauteur: number },
  mode: AjustementScene = "cover",
  ancrageVertical: AncrageVertical = "haut",
  reserveBasPx = 0,
): { x: number; y: number } {
  if (boite.largeur <= 0 || boite.hauteur <= 0) return clampePourcent(pourcentImage);
  const { largeur: largeurAffichee, hauteur: hauteurAffichee } = rectangleImageAffichee(boite, mode);
  const decalageX = (largeurAffichee - boite.largeur) / 2;
  // `contain` et l'échelle explicite ancrent l'image en HAUT (repli `"haut"`), en BAS (`"bas"`,
  // ⟦C26 bis · point 3⟧) ou au CENTRE (`"centre"`, ⟦C26 ter · point 1⟧) de la boîte, jamais
  // centrée par défaut (⟦C21 ter⟧). Seul `cover` centre verticalement (recadrage symétrique),
  // quel que soit `ancrageVertical`. `reserveBasPx` (⟦point 1⟧) ne vaut que pour `"bas"` : décale
  // le bord bas de l'image de `reserveBasPx` px VERS LE HAUT, comme si la boîte était plus
  // courte d'autant.
  const decalageY =
    mode === "cover" || ancrageVertical === "centre"
      ? (hauteurAffichee - boite.hauteur) / 2
      : ancrageVertical === "bas"
        ? hauteurAffichee - boite.hauteur + reserveBasPx
        : 0;
  const xPixel = (pourcentImage.x / 100) * largeurAffichee - decalageX;
  const yPixel = (pourcentImage.y / 100) * hauteurAffichee - decalageY;
  return clampePourcent({ x: (xPixel / boite.largeur) * 100, y: (yPixel / boite.hauteur) * 100 });
}

/**
 * ⟦C27 ter⟧ **Le rectangle RÉELLEMENT dessiné de la pièce, en pixels DE BOÎTE** (coin haut-gauche
 * + largeur/hauteur), pas celui de l'élément qui la contient. Les deux coins extrêmes de l'image
 * (0,0) et (100,100), reconvertis en % de boîte par `positionDansBoite` — exactement comme
 * n'importe quel hotspot — puis en pixels : en mode `cover`, les deux coins retombent toujours
 * sur (0,0) et (100,100) (aucune bande, le rectangle vaut la boîte entière) ; en mode `contain`/
 * échelle explicite (mobile), le coin bas-droit peut retomber AVANT 100 % dans un sens ou
 * l'autre (le vide comblé par `couleurCoinChambre`, jamais recadré) — c'est exactement le
 * rectangle qui manquait à `Accueil.tsx` : avant ce chantier, le glissement mobile interpolait
 * vers le rectangle de LA BOÎTE (`.scene-mobile-zone`, pleine largeur d'un bout à l'autre), pas
 * vers celui de l'image dedans, qui passe donc d'un coup du plein écran zoomé de l'accueil au
 * cadrage mobile ancré une fois la vraie scène révélée (constat du chantier, la pièce reste
 * plein écran sept images puis saute). Interpoler ce rectangle-ci, pas celui de la boîte,
 * supprime le saut : sur ordinateur (`cover`), il est identique au rectangle de la boîte, donc
 * aucun changement (`Accueil.tsx` ne l'utilise que sur mobile, mais la fonction reste correcte
 * dans les deux modes, vérifié par test).
 */
export function rectangleImagePixels(
  boite: { largeur: number; hauteur: number },
  mode: AjustementScene,
  ancrageVertical: AncrageVertical = "haut",
): { x: number; y: number; largeur: number; hauteur: number } {
  const coinHautGauche = positionDansBoite({ x: 0, y: 0 }, boite, mode, ancrageVertical);
  const coinBasDroit = positionDansBoite({ x: 100, y: 100 }, boite, mode, ancrageVertical);
  const x = (coinHautGauche.x / 100) * boite.largeur;
  const y = (coinHautGauche.y / 100) * boite.hauteur;
  return {
    x,
    y,
    largeur: ((coinBasDroit.x - coinHautGauche.x) / 100) * boite.largeur,
    hauteur: ((coinBasDroit.y - coinHautGauche.y) / 100) * boite.hauteur,
  };
}

/**
 * ⟦C21 ter · point 1⟧ **La couleur de coin de chaque planche**, relevée à la pipette (moyenne
 * d'un carré de 8×8 px au coin bas-gauche de l'image source, le coin adjacent à la zone vide
 * quand `contain` ancre l'image en haut : c'est ce bord-là qui doit se fondre dans le
 * remplissage, pas le haut). `chambre-nX-jour` et `chambre-nX-nuit` partagent le même fichier
 * PNG pour chaque niveau (`md5` identique, vérifié, comme le bureau de n1, ⟦A-146⟧) : dix clés
 * dans cette table, cinq couleurs distinctes. Posée en `background-color` par `Chambre.tsx`
 * quand la scène est en mode `contain`, sans effet en mode `cover`, qui remplit toujours 100 %
 * de sa boîte et ne laisse donc jamais voir le fond.
 */
const COULEUR_COIN_PAR_SCENE: Readonly<Record<string, string>> = {
  // ⟦C26⟧ Remesuré sur la nouvelle planche de base (candidat Higgsfield 32) : #193b63, très
  // proche de l'ancien #1b395f (même convention de bleu marine), mais pas identique — la
  // planche a changé, la mesure suit.
  "chambre-n1-jour": "#193b63",
  "chambre-n1-nuit": "#193b63",
  "chambre-n2-jour": "#2d4774",
  "chambre-n2-nuit": "#2d4774",
  "chambre-n3-jour": "#1e2b4b",
  "chambre-n3-nuit": "#1e2b4b",
  "chambre-n4-jour": "#1b3351",
  "chambre-n4-nuit": "#1b3351",
  "chambre-n5-jour": "#20285d",
  "chambre-n5-nuit": "#20285d",
};

/** Repli si jamais une clé manquait (jamais aujourd'hui, les dix existent) : le même
 *  `--jaune-profond` que `.chambre-scene` utilisait déjà comme fond par défaut. */
const COULEUR_COIN_REPLI = "var(--jaune-profond)";

/** La couleur de fond à poser derrière la scène en mode `contain` (mobile), voir
 *  `COULEUR_COIN_PAR_SCENE`. */
export function couleurCoinChambre(scene: string): string {
  return COULEUR_COIN_PAR_SCENE[scene] ?? COULEUR_COIN_REPLI;
}

/**
 * ⟦C21 quater · point 2⟧ **La pièce agrandie sur mobile, planche par planche.**
 *
 * Constat du chantier : en `contain` pur (cale sur la largeur, `Math.min`), la pièce de n1
 * n'occupe que 220 px de haut sur une colonne de 600-720 px, l'image a ses propres marges
 * bleues sur les côtés, « on peut l'agrandir sans rogner la pièce ». Le chantier propose
 * `≈135 %` et demande de « caler » : mesuré (pas deviné, ⟦Consigne 29/08⟧) plutôt que deviné,
 * **cette marge n'est PAS la même sur les cinq planches** (la pièce de n5, le plateau final,
 * remplit déjà la largeur de l'image quasiment bord à bord, celle de n1 n'en occupe qu'environ
 * 56 %). Un seul facteur pour les cinq aurait soit laissé n1 minuscule, soit rogné le canapé de
 * n4 ou l'écran de n5 hors champ. D'où une table, comme `COULEUR_COIN_PAR_SCENE`.
 *
 * **Méthode de mesure** (script `mesure-largeur-piece.cjs`, hors dépôt, balayage de lignes
 * horizontales sur chaque planche source, seuil de distance couleur au coin bas-gauche relevé
 * par `couleurCoinChambre`, la largeur retenue est la plus grande ligne non-fond trouvée) :
 * fraction de la largeur de l'image (1024 px) réellement occupée par la pièce dessinée,
 * n1 : 0,564 · n2 : 0,726 · n3 : 0,754 · n4 : 0,688 · n5 : 0,999 (bord à bord, aucune marge).
 * Le facteur retenu par planche vise une pièce qui occupe ~95,6 % de la largeur de la BOÎTE
 * (marge ≈ 8 px de chaque côté sur une boîte de 366 px, la mesure du chantier à 390×844) :
 * `facteur = 0,956 / fraction`. n5 tombe donc SOUS 1 (0,957), moins que le simple
 * `Math.min` de `contain`, puisqu'elle n'a besoin d'aucun agrandissement, juste d'une toute
 * petite marge pour ne jamais coller la pièce au bord vif de l'écran.
 *
 * ⟦C26⟧ **n1 remesuré** sur la nouvelle planche de base (même méthode, seuil de distance
 * couleur au coin bas-gauche relevé par `couleurCoinChambre`) : la pièce y occupe une fraction
 * de 0,696 (713 px sur 1 024) — bien PLUS que l'ancienne planche (0,564) —, donc un facteur
 * PLUS PETIT : `0,956 / 0,696 ≈ 1,373` (était 1,694). La nouvelle pièce a moins de marge bleue
 * à rogner, elle a besoin de moins de zoom pour remplir la largeur de la boîte mobile.
 */
const ECHELLE_PIECE_MOBILE_PAR_SCENE: Readonly<Record<string, number>> = {
  "chambre-n1-jour": 1.373,
  "chambre-n1-nuit": 1.373,
  "chambre-n2-jour": 1.318,
  "chambre-n2-nuit": 1.318,
  "chambre-n3-jour": 1.268,
  "chambre-n3-nuit": 1.268,
  "chambre-n4-jour": 1.391,
  "chambre-n4-nuit": 1.391,
  "chambre-n5-jour": 0.957,
  "chambre-n5-nuit": 0.957,
};

/** Repli si jamais une clé manquait (jamais aujourd'hui, les dix existent) : `1`, l'ancien
 *  comportement `contain` pur (cale sur la largeur, aucun agrandissement, aucun recadrage). */
const ECHELLE_PIECE_MOBILE_REPLI = 1;

/**
 * Le facteur d'agrandissement mobile pour CETTE scène : multiplie l'échelle « cale sur la
 * largeur » de `rectangleImageAffichee`/`positionDansBoite` (mode numérique, voir plus haut).
 * `Chambre.tsx` le résout à la place du mode `"contain"` littéral dès qu'une scène est connue :
 * l'appelant (`Studio.tsx`) continue de demander `"contain"` (« montre la pièce entière,
 * mobile »), Chambre.tsx choisit COMMENT, planche par planche.
 */
export function echellePieceMobile(scene: string): number {
  return ECHELLE_PIECE_MOBILE_PAR_SCENE[scene] ?? ECHELLE_PIECE_MOBILE_REPLI;
}

/**
 * ⟦C23 §3⟧ **La position de l'écran du moniteur, relevée planche par planche.**
 *
 * Retour de Matt (la 3e fois) : « le personnage qui va au bureau ne matche pas [dans le
 * bureau]. » Décision de méthode (Système D) : plutôt que de recaler un sprite assis à un angle
 * qu'aucune planche ne dessine pareil, l'avatar debout disparaît pendant `travail` et c'est
 * l'ÉCRAN du bureau qui s'allume à sa place (`Chambre.tsx`). Encore faut-il savoir où est cet
 * écran : le hotspot `bureau` de `HOTSPOTS_PAR_SCENE` cible le meuble entier (souvent son
 * tiroir du bas, pensé pour un CLIC, pas pour un moniteur), pas l'écran lui-même.
 *
 * Relevé à l'œil sur chaque planche source (crop + zoom, jamais deviné) : trois planches sur
 * cinq dessinent un écran distinct : n1 (l'écran du moniteur sur le bureau, 76,5 ; 50 — remesuré
 * ⟦C26⟧ sur la nouvelle planche de base, l'ancien point (61,5 ; 26) visait le moniteur mural de
 * l'ANCIENNE planche, qui n'existe plus), n3 (l'écran du bureau ovale sous la fenêtre, 53,6 ;
 * 38,8), n4 (l'écran du bureau en bois, 51,8 ; 46). Les fichiers jour/nuit d'un même niveau sont
 * pixel-identiques (`md5`, comme `COULEUR_COIN_PAR_SCENE`) : une seule mesure sert aux deux clés.
 * `chambre-n2-*` (aucun bureau dessiné, cf. le tableau plus haut) et `chambre-n5-*` (un bureau nu,
 * sans moniteur visible, le plateau final mise sur la télé murale, pas un écran de travail) n'ont
 * pas d'entrée : `Chambre.tsx` n'affiche alors pas le rectangle qui clignote, seule la bulle
 * « ▶ REC » reste, ancrée sur le hotspot `bureau` lui-même (ou son repli par défaut).
 *
 * ⟦C26 · C34⟧ Sur `chambre-n1-*`, cette table (et les bulles « z z z »/« ▶ REC ») ne sert plus QUE
 * pour une partie sans genre connu (`state.genre === null`, parties d'avant ⟦A-4 §2⟧) :
 * `calquePoseChambreN1` (plus bas) couvre désormais `sommeil`/`travail` par un calque personnage
 * pour tout genre connu, et `Chambre.tsx` n'affiche alors ni sprite ni ces effets (voir plus bas).
 * Gardée à jour plutôt que laissée fausse, pour ce cas résiduel.
 */
const POSITION_ECRAN_PAR_SCENE: Readonly<Record<string, { x: number; y: number }>> = {
  "chambre-n1-jour": { x: 76.5, y: 50 },
  "chambre-n1-nuit": { x: 76.5, y: 50 },
  "chambre-n3-jour": { x: 53.6, y: 38.8 },
  "chambre-n3-nuit": { x: 53.6, y: 38.8 },
  "chambre-n4-jour": { x: 51.8, y: 46 },
  "chambre-n4-nuit": { x: 51.8, y: 46 },
};

/** `null` : cette planche ne dessine pas d'écran distinct du hotspot `bureau` (n2, n5). */
export function positionEcranBureau(scene: string): { x: number; y: number } | null {
  return POSITION_ECRAN_PAR_SCENE[scene] ?? null;
}

/**
 * ⟦C26 · A-150⟧ **Les calques par état : le personnage est peint dans la planche, plus superposé.**
 *
 * Décision de DA (Système D, suite directe de ⟦C23 §3⟧) : les sprites `assis`/`couché`
 * (`heros.ts`) ne matchaient aucune planche de fond, et l'extraction du personnage par
 * différence (`scripts/extraire-sprite-diff.py`) s'est révélée trop bruitée pour la production
 * (le tapis et les rideaux se re-rendent à chaque génération). Plutôt qu'un sprite recalé, GPT
 * Image 2 régénère la CHAMBRE ENTIÈRE avec le personnage peint dedans, une planche par
 * situation — sept situations (`PoseEtat`) × deux genres, pour l'instant `chambre-n1` seulement
 * (les autres niveaux gardent le sprite superposé, ⟦C14 · A-133⟧, inchangé).
 *
 * `debout` (idle), `lit` (sommeil), `bureau` (travail) couvrent les trois poses de JEU ; `lit`,
 * `bureau`, `fenetre`, `tableau` couvrent aussi les survols ⟦A-139⟧ des quatre objets qui font
 * suivre l'avatar ou changent son cadrage — les noms de pose et les ids d'objet de la chambre
 * sont volontairement les MÊMES chaînes (`lit`, `bureau`, `fenetre`, `tableau`), pas de table de
 * correspondance séparée à maintenir en double. `commode` et `telephone` ont leur planche
 * (produite pour les sept situations, cf. `scripts/pipeline_c26.py` en note de chantier) mais
 * n'ont pas encore de hotspot dédié sur `chambre-n1-jour` (décision de Mathieu en conversation,
 * 13/09 : la commode et le tapis ne sont pas cliquables pour l'instant) — la table les couvre
 * déjà pour le jour où un hotspot leur sera posé, sans nouveau chantier sur ce fichier.
 */
export type PoseEtat = "lit" | "bureau" | "fenetre" | "tableau" | "commode" | "telephone" | "debout";

/** Les sept situations peintes — exporté pour le préchargement (`Chambre.tsx`, §3 du chantier :
 *  « les 7 planches du genre joué sont préchargées à l'entrée en chambre »). */
export const POSES_ETAT: readonly PoseEtat[] = ["lit", "bureau", "fenetre", "tableau", "commode", "telephone", "debout"];

/** Un id d'objet de la chambre est-il aussi le nom d'une pose d'état (survol → planche) ? */
export function estPoseEtat(id: string): id is PoseEtat {
  return (POSES_ETAT as readonly string[]).includes(id);
}

/**
 * ⟦C33 · A-158 · C40 · A-163 · C40 bis · A-163 · C41 · A-164⟧ **`tendances` n'a pas de planche
 * peinte PARMI LES SEPT** (convention ⟦C26⟧ : l'id de l'objet EST le nom de sa pose — `lit`,
 * `bureau`, `fenetre`, `tableau`… — `estPoseEtat` le vérifie pour ces sept-là). `PoseAffichee`
 * élargit `PoseEtat` d'une valeur synthétique, `"tendances"`, pour une HUITIÈME situation qui a
 * bien ses propres calques (`calquePoseChambreN1`, plus bas) mais jamais sa propre planche
 * archivée dans `originaux/` — sa source est une génération DÉDIÉE, pas une des dix planches de
 * ⟦C29⟧.
 *
 * **Deux tentatives économiques, deux échecs mesurés, avant la planche dédiée :**
 * - ⟦C40⟧ : translater le calque `tableau` ENTIER (effacement + personnage encore confondus à
 *   l'époque) du vecteur liège→tendances — déplaçait aussi l'effacement, le personnage du repos
 *   réapparaissait au milieu de la pièce.
 * - ⟦C40 bis⟧ : la séparation effacement/personnage (`scripts/chambre-calques-effacement-
 *   c40bis.py`) corrige CE défaut-là (vérifié, écart nul pour `lit`/`bureau`/`fenetre`/`tableau`)
 *   mais en révèle un second, plus profond : le calque « personnage » de `tableau` contient le
 *   personnage **ET** le panneau de liège regénéré ENTIER (« le personnage + son meuble », correct
 *   pour SON survol, mais ce panneau ne doit pas voyager vers le mur des tendances qui a déjà son
 *   propre meuble peint). Rectangle d'exclusion, érosion morphologique, palette de couleur :
 *   essayés et mesurés sans succès (rapport `assets/making-of/2026-09-18-C40-mur/bis/rapport/`).
 *   ⟦A-163⟧ a arrêté là plutôt que de bricoler, `poseEtatDeObjet("tendances")` retombait alors sur
 *   `"tableau"` sans translation — la seule échappatoire sûre tant qu'aucun asset dédié n'existait.
 *
 * ⟦C41 · A-164⟧ **La planche dédiée, décidée par Matt (18/09, verbatim : « 1 », après le rapport
 * ⟦C40 bis⟧) : générée avec la chambre EN RÉFÉRENCE** (Higgsfield GPT Image 2, `fond-{genre}.png`
 * + `calque-tendances-{genre}.png` composés, jamais le fond seul) — elle ne contient donc JAMAIS
 * le panneau de liège, le défaut de ⟦C40 bis⟧ ne peut structurellement plus se produire. Calée sur
 * le fond (ORB/AKAZE, `scripts/chambre-pose-tendances-c41.py`, même outillage que ⟦C29⟧/⟦C34⟧),
 * composée (⟦A-155⟧, écart nul mesuré hors de la zone du personnage) puis découpée en
 * `effacement`/`personnage` par le MÊME algorithme que ⟦C40 bis⟧ (composante par composante,
 * classée par proximité à `REPOS_AVATAR_PCT`/`ANCRE_POSE_PCT["tendances"]` de
 * `_alignement_chambre_commun.py` — cette dernière ajoutée par ce chantier, reprise telle quelle
 * de `HOTSPOTS_PAR_SCENE["chambre-n1-jour"].tendances` ci-dessus). `"tendances"` a maintenant ses
 * PROPRES rectangles dans `RECT_CALQUE_POSE_EFFACEMENT`/`RECT_CALQUE_POSE_PERSONNAGE` (plus bas),
 * jamais plus ceux de `"tableau"` : `poseEtatDeObjet("tendances")` renvoie `"tendances"` elle-même.
 */
export type PoseAffichee = PoseEtat | "tendances";

/** La pose affichée pour CET id d'objet, ou `null` s'il n'en a aucune. Un objet qui a bien sa
 *  propre planche (`estPoseEtat`) s'y réduit à l'identité. ⟦C41 · A-164⟧ `tendances` a maintenant
 *  ses propres calques (voir le commentaire de `PoseAffichee`) : elle se réduit aussi à
 *  l'identité, comme les sept planches peintes — plus de repli sur `"tableau"`. */
export function poseEtatDeObjet(id: string): PoseAffichee | null {
  if (id === "tendances") return "tendances";
  return estPoseEtat(id) ? id : null;
}

/** Les trois poses de JEU (`heros.ts`) qui ont un équivalent planche d'état — `victoire` n'en a
 *  aucune (elle garde son sprite R3 dédié, inchangé, ⟦C14⟧). */
const POSE_ETAT_PAR_POSE_HEROS: Partial<Record<PoseHeros, PoseEtat>> = {
  idle: "debout",
  sommeil: "lit",
  travail: "bureau",
};

/** La pose d'état pour la pose de JEU courante — `null` pour `victoire`, comme l'absence de
 *  planche d'état pour ce cas (`Chambre.tsx` retombe alors sur le sprite R3 de célébration). */
export function poseEtatDeBase(pose: PoseHeros): PoseEtat | null {
  return POSE_ETAT_PAR_POSE_HEROS[pose] ?? null;
}

/**
 * ⟦C32 · A-157⟧ **La correspondance bouton d'action → objet de la chambre.** Demande de Matt du
 * 16/09, docs/chantiers/C32-les-boutons-bougent-l-avatar.md : « je voudrais que les boutons
 * d'action en bas changent aussi la position de l'avatar, comme au hover et clic sur l'image. »
 *
 * Table PURE (aucun JSX, aucun hook), à côté du reste de ce module plutôt que dans `Studio.tsx` :
 * elle se teste sans rendu (`chambre.test.ts`), et un id qui dérive d'un côté (cette table) sans
 * l'autre (`Studio.tsx`, la table `objets`, ou `Chambre.tsx`) casse un test plutôt qu'un
 * comportement silencieusement oublié. `Studio.tsx` la consomme pour poser les gestionnaires de
 * survol/focus/toucher de chaque bouton (`survolBoutonHandlers`), `Chambre.tsx` reçoit l'id
 * retenu en `survolExterne`.
 *
 * Ne couvre QUE les boutons qui ont un objet réellement peint dans la chambre (`Studio.tsx`, table
 * `objets`) : « un bouton sans objet peint dans la chambre ne change rien à la scène » (attendu du
 * chantier).
 *
 * ⟦C60 · A-177⟧ **Plus aucun bouton sans objet.** Le grand ménage a retiré Bibliothèque, Boutique, Logement
 * et Jalons (leurs trois objets flottants, 📚 🏦 📦, partent avec eux : ils n'étaient pas peints, c'étaient
 * des emojis posés sur la pièce), et il a fallu rattacher ce qui reste : **l'Équipe et le Marché au
 * bureau** (le survol du bouton allume le bureau, comme pour Publier), le Guide et le Palmarès au bureau
 * aussi (ils se consultent à l'écran). Le panneau de liège garde l'Arbre, le téléphone les commentaires,
 * la fenêtre la veille, et le lit Souffler (le bouton du bandeau de santé mentale : une pause, c'est un
 * repos) : décision de Matt (03/10, `docs/chantiers/C60-le-grand-menage.md`).
 */
// ⟦C33 · A-158⟧ `arbre: "tableau"` — le panneau de liège cesse d'être un objet séparé (`liege`,
// pastille 🌳 à une position inventée, décision de Matt du 17/09) : c'est désormais l'objet
// `tableau` lui-même (le panneau à post-it dessiné dans le fond) qui ouvre l'arbre au clic. Le
// survol du bouton « Arbre » du bas montre donc la MÊME planche que le survol direct du panneau
// — c'était déjà vrai avant ce chantier pour `dormir`/lit et `publier`/bureau, ce n'est qu'un
// troisième id qui rejoint la règle, pas un mécanisme nouveau.
export const OBJET_PAR_BOUTON_ACTION = {
  publier: "bureau",
  dormir: "lit",
  veille: "fenetre",
  commentaires: "telephone",
  arbre: "tableau",
  // ⟦C60 · A-177⟧ Ce qui reste d'« Actions » se rattache à un objet dessiné : le bureau pour tout ce qui
  // se gère à l'écran (l'équipe, le marché, le guide, le palmarès).
  equipe: "bureau",
  marche: "bureau",
  guide: "bureau",
  palmares: "bureau",
  // Souffler (le bandeau de santé mentale de « Ma chaîne ») : une pause, c'est le lit.
  souffler: "lit",
} as const;

/**
 * ⟦C34 · A-159⟧ **`chambre-n1` en calques : un fond fixe, plus des calques posés dessus.**
 *
 * Demande de Matt (17/09, docs/chantiers/C34-la-chambre-en-calques.md) : « quand je vais sur le
 * lit, il n'y a vraiment que la zone du lit qui change [...] au bureau, il y a le cadre de la
 * trend qui bouge [...] est-ce que tu peux faire que le panneau de trend, au départ, ce soit des
 * cadres photo ? » Le défaut (⟦C26 · A-150⟧, ⟦C33 · A-158⟧) : chaque planche d'état était une
 * image ENTIÈRE régénérée par situation (GPT Image 2 redessine toute la pièce), donc le mur
 * (liège, tableau des tendances) n'y est jamais strictement identique d'une planche à l'autre —
 * survoler un meuble « bouge » le mur, même sans le toucher.
 *
 * Architecture retenue : un **fond fixe par genre** (`fondChambreN1`, la pièce de repos, liège
 * VIDE, les DEUX CADRES PHOTO d'origine intacts, jamais un troisième état de mur peint dedans),
 * plus jusqu'à trois calques RGBA posés dessus, tous rognés à leur boîte englobante (jamais
 * l'image entière) :
 * - `calqueLiegeChambreN1` (les post-it), posé seulement si `ouvert(state, "arbre")` ;
 * - `calqueTendancesChambreN1` (le tableau blanc, à la place des deux cadres), posé seulement si
 *   `ouvert(state, "tendances")` — EXACTEMENT la même condition que celle qui rend l'objet
 *   `tendances` cliquable dans `Studio.tsx`, jamais un second seuil à maintenir en double ;
 * - `calquePoseChambreN1` (le personnage + son meuble, l'ancien contenu des planches d'état),
 *   posé pour la pose courante (survol, focus, bouton du bas, sommeil, travail) — `null` pour
 *   `debout` (le fond EST déjà cette pose) et pour `commode` (jamais atteignable en jeu, aucun
 *   calque produit, même convention que `scripts/_alignement_chambre_commun.py`).
 *
 * ⟦C40 bis · A-163⟧ **Le calque de pose est lui-même DEUX calques depuis ce chantier** :
 * `effacement` (la zone où le personnage DEBOUT du repos doit disparaître, repeinte avec le
 * décor — ne bouge JAMAIS, posée à sa position NATIVE quelle que soit la pose affichée) et
 * `personnage` (le personnage + son meuble, qui PEUT être posé ailleurs). Motif : `calquePoseChambreN1`
 * fabriquait un calque UNIQUE par différence brute avec `debout` (`scripts/chambre-calques-c34.py`)
 * — translater ce calque entier (tenté pour `"tendances"`, voir plus bas) déplaçait aussi
 * l'effacement, faisant réapparaître le personnage du repos au milieu de la pièce. Séparés une
 * fois pour toutes dans `scripts/chambre-calques-effacement-c40bis.py` (composante par
 * composante du même masque de diff, classée par proximité aux deux points d'ancrage déjà
 * mesurés de `_alignement_chambre_commun.py` — repos vs pose ; la seule composante FUSIONNÉE
 * rencontrée, `f-bureau`, est scindée pixel par pixel). `effacement ∪ personnage` reproduit,
 * posé à sa place NATIVE, EXACTEMENT l'ancien calque unique (écart nul, vérifié pour les 10
 * combinaisons genre × pose) : `lit`/`bureau`/`fenetre`/`tableau` (le survol du liège) ne
 * changent donc pas d'un pixel après ce chantier.
 *
 * Chaque calque ne peut, PAR CONSTRUCTION, dessiner que dans sa propre boîte englobante : rien
 * en dehors ne peut changer au survol, quelle que soit la combinaison de calques actifs —
 * exactement la demande de Matt, et le seul moyen de la garantir géométriquement plutôt qu'en
 * espérant que chaque régénération future reste identique aux autres.
 *
 * **Provenance** (`scripts/chambre-calques-c34.py`, relançable, Pillow + NumPy + SciPy, jamais
 * un modèle génératif) : le fond et les calques liège/pose sont des COPIES ou DIFFÉRENCES de
 * pixels déjà produits et validés (les 16 planches d'avant le branchement du mur de ⟦C33⟧,
 * archivées dans `assets/making-of/2026-09-17-C33-branchement/originaux/`, ALIGNÉES par
 * `corriger-alignement-chambre.py` ⟦C29⟧ — identiques au pixel près à la planche de repos hors
 * la zone personnage/meuble, un diff brut suffit donc à retrouver cette zone sans refaire le
 * recalage) ; le calque tendances vient du tableau déjà collé et validé par Matt en ⟦C33⟧.
 * Vérifié par recomposition en mémoire (rapport `assets/making-of/2026-09-17-C34-calques/
 * rapport-generation.json`) : fond + calque liège reproduit `originaux/…` À ZÉRO ÉCART pour les
 * 10 combinaisons genre × pose, et fond + calque de pose (mur fermé) ne dévie JAMAIS hors de
 * l'alpha du calque de pose — le critère même de ce chantier, mesuré, pas juste annoncé.
 *
 * Les rectangles ci-dessous (en % de l'image 1024×576) sont ceux mesurés par ce script : s'ils
 * bougent un jour (nouvelle génération), les retaper aux DEUX endroits (le script les imprime).
 */
const RECT_CALQUE_LIEGE: Readonly<Record<string, EmpriseObjet>> = {
  "": { x0: 21.387, y0: 25.347, x1: 33.008, y1: 46.528 },
  f: { x0: 21.973, y0: 23.438, x1: 33.008, y1: 43.75 },
  h: { x0: 21.289, y0: 25.347, x1: 33.398, y1: 46.528 },
};

const RECT_CALQUE_TENDANCES: Readonly<Record<string, EmpriseObjet>> = {
  "": { x0: 34.375, y0: 19.618, x1: 43.457, y1: 38.715 },
  f: { x0: 34.375, y0: 18.229, x1: 42.773, y1: 37.153 },
  h: { x0: 34.473, y0: 19.792, x1: 43.359, y1: 38.715 },
};

/** `commode` n'a pas d'entrée : jamais atteignable en jeu (aucun id `commode` dans `objets`,
 *  `Studio.tsx`), aucun calque produit — même convention que `POSES` de
 *  `_alignement_chambre_commun.py`. `debout` non plus : c'est le fond lui-même, aucun calque à
 *  poser par-dessus.
 *
 * ⟦C40 bis · A-163⟧ Les cinq premières entrées de chaque table sont MESURÉES par
 * `scripts/chambre-calques-effacement-c40bis.py` (chaque calque rogné à SA propre boîte
 * englobante, plus petite que l'ancien calque unique puisqu'il ne porte plus que sa moitié — voir
 * le commentaire au-dessus de `calquePoseChambreN1`). `RECT_CALQUE_POSE_EFFACEMENT` : la zone à
 * repeindre (personnage du repos qui disparaît), toujours posée à sa position NATIVE, jamais
 * translatée. `RECT_CALQUE_POSE_PERSONNAGE` : le personnage + son meuble — `f.telephone` n'a PAS
 * d'entrée : mesuré à zéro pixel « personnage » pour cette pose (le personnage ne bouge presque
 * pas du repos pour téléphoner, toute la différence est retombée côté effacement),
 * `calquePoseChambreN1` renvoie alors `personnage: null` pour ce cas précis — l'effacement seul
 * suffit à reproduire l'ancien calque.
 *
 * ⟦C41 · A-164⟧ **`tendances` rejoint les deux tables, MESURÉE par
 * `scripts/chambre-pose-tendances-c41.py`** sur sa propre planche générée (calée, composée,
 * découpée — voir le commentaire de `PoseAffichee`, plus haut) : jamais plus une translation du
 * rectangle `tableau`. D'où le type `PoseAffichee` (pas `PoseEtat`) de ces deux tables : la seule
 * clé qui n'est pas une des sept planches peintes. */
const RECT_CALQUE_POSE_EFFACEMENT: Readonly<Record<GenreAvatar, Readonly<Partial<Record<PoseAffichee, EmpriseObjet>>>>> = {
  f: {
    lit: { x0: 46.68, y0: 27.604, x1: 54.688, y1: 75.174 },
    bureau: { x0: 34.668, y0: 27.604, x1: 62.988, y1: 75.694 },
    fenetre: { x0: 32.52, y0: 27.604, x1: 60.449, y1: 93.056 },
    tableau: { x0: 33.398, y0: 27.604, x1: 61.426, y1: 75.521 },
    telephone: { x0: 45.215, y0: 27.604, x1: 54.688, y1: 66.84 },
    tendances: { x0: 35.938, y0: 41.493, x1: 66.992, y1: 91.667 },
  },
  h: {
    lit: { x0: 44.531, y0: 34.028, x1: 56.543, y1: 73.264 },
    bureau: { x0: 46.777, y0: 34.028, x1: 56.543, y1: 71.181 },
    fenetre: { x0: 47.949, y0: 34.028, x1: 56.543, y1: 71.181 },
    tableau: { x0: 48.047, y0: 34.028, x1: 56.543, y1: 78.819 },
    telephone: { x0: 46.582, y0: 34.028, x1: 56.543, y1: 73.264 },
    tendances: { x0: 48.145, y0: 34.549, x1: 62.695, y1: 79.688 },
  },
};

const RECT_CALQUE_POSE_PERSONNAGE: Readonly<Record<GenreAvatar, Readonly<Partial<Record<PoseAffichee, EmpriseObjet>>>>> = {
  f: {
    lit: { x0: 18.652, y0: 44.618, x1: 44.629, y1: 76.042 },
    bureau: { x0: 61.035, y0: 32.986, x1: 78.613, y1: 72.222 },
    fenetre: { x0: 58.203, y0: 12.153, x1: 76.27, y1: 58.333 },
    tableau: { x0: 20.605, y0: 18.924, x1: 39.258, y1: 60.069 },
    // telephone : absente — voir le commentaire au-dessus de la table.
    tendances: { x0: 32.422, y0: 18.403, x1: 53.711, y1: 51.562 },
  },
  h: {
    lit: { x0: 19.629, y0: 52.431, x1: 35.059, y1: 76.91 },
    bureau: { x0: 65.82, y0: 27.431, x1: 85.742, y1: 76.215 },
    fenetre: { x0: 59.473, y0: 17.535, x1: 71.094, y1: 60.938 },
    tableau: { x0: 27.344, y0: 29.688, x1: 36.133, y1: 61.979 },
    telephone: { x0: 17.871, y0: 60.243, x1: 20.508, y1: 67.708 },
    tendances: { x0: 36.426, y0: 22.743, x1: 45.605, y1: 59.028 },
  },
};

/** La clé de table pour un genre — `""` (sans genre) sert de clé aux rects `RECT_CALQUE_*`
 *  ci-dessus, `"sans-genre"` au NOM DE FICHIER (`chambre-calques-c34.py` écrit les deux
 *  distinctement, la chaîne vide n'est pas un joli nom de fichier). */
function cleRectGenre(genre: GenreAvatar | null | undefined): string {
  return genre ?? "";
}

/** Le fond fixe de `chambre-n1` pour ce genre (ou sans genre, parties d'avant ⟦A-4 §2⟧, et le
 *  fond de `FondMenu.tsx` qui n'a pas de genre) : la pièce de repos, liège VIDE, les deux
 *  cadres photo d'origine intacts — ne change plus jamais avec la pose ni le déblocage du mur,
 *  ceux-ci vivent désormais dans les calques ci-dessous. */
export function fondChambreN1(genre: GenreAvatar | null | undefined): string {
  return genre ? `/scenes/chambre-n1-fond-${genre}.png` : "/scenes/chambre-n1-fond.png";
}

/** Un calque posé sur le fond : son chemin RGBA et le rectangle (en % de l'image) où le poser,
 *  toujours converti par le MÊME calcul que les hotspots (`styleEmpriseHotspot`, plus haut) pour
 *  rester aligné au dessin quel que soit le recadrage (`cover`/`contain`/échelle mobile). */
export interface CalqueChambreN1 {
  readonly chemin: string;
  readonly rect: EmpriseObjet;
}

/** ⟦C40 bis · A-163⟧ Ce que renvoie `calquePoseChambreN1` : les DEUX calques d'une pose, jamais
 *  un seul — voir son commentaire, plus bas, pour le pourquoi de la séparation. `effacement` se
 *  pose TOUJOURS à sa position native (jamais translaté, même pour `"tendances"`) ;
 *  `personnage` peut l'être. L'un ou l'autre (jamais les deux) peut valoir `null` — `personnage`
 *  pour `f`/`telephone` (mesuré à zéro pixel, voir `RECT_CALQUE_POSE_PERSONNAGE`), jamais
 *  `effacement` (toute pose atteignable a un personnage du repos à effacer). */
export interface CalquesPoseChambreN1 {
  readonly effacement: CalqueChambreN1 | null;
  readonly personnage: CalqueChambreN1 | null;
}

/** Le calque « liège rempli » (TOUS les post-it à la fois) — gardé pour compatibilité (encore
 *  testé, encore un fichier livré) mais plus appelé par `Chambre.tsx` depuis ⟦C40⟧ : voir
 *  `calquesLiegeChambreN1`, juste en dessous, qui affiche les post-it un par un. */
export function calqueLiegeChambreN1(genre: GenreAvatar | null | undefined): CalqueChambreN1 {
  return {
    chemin: `/scenes/chambre-n1-calque-liege-${genre ?? "sans-genre"}.png`,
    rect: RECT_CALQUE_LIEGE[cleRectGenre(genre)]!,
  };
}

/**
 * ⟦C40 · A-163⟧ **Les post-it individuels du panneau de liège, dans un ordre de lecture fixe.**
 * Retour de Matt (18/09) : « les post-it, on a dit c'est pour l'arbre de compétences, du coup
 * genre il se complète au fur et à mesure que tu prends des compétences. » Avant ce chantier,
 * `calqueLiegeChambreN1` posait TOUS les post-it d'un coup dès `ouvert(state, "arbre")` — plus
 * jamais depuis ce point : le panneau gagne un post-it par compétence prise.
 *
 * **Découpe** (`scripts/chambre-postits-c40.py`, relançable, Pillow + NumPy + SciPy, jamais un
 * modèle génératif) : chaque post-it INDIVIDUEL est une intersection entre (a) sa propre région
 * (détection par couleur de `mur-veille-branchement.py` ⟦C33⟧, puis séparation watershed-lite
 * scipy pour dégroupper les post-it qui se touchent — une simple composante connexe brute en
 * fusionne plusieurs) et (b) le calque « liège rempli » déjà validé et livré (`RECT_CALQUE_LIEGE`,
 * ci-dessus) : zéro pixel hors de ce qui est déjà à l'écran aujourd'hui. Le nombre RÉEL de post-it
 * diffère par planche (mesuré, jamais deviné — les trois planches ne dessinent pas exactement les
 * mêmes post-it) : `sans-genre` 13, `f` 19, `h` 16 (rapport `assets/making-of/
 * 2026-09-18-C40-mur/rapport-postits.json`).
 *
 * **Ordre** : de lecture (haut en bas, puis gauche à droite), figé dans les NOMS de fichiers par
 * le script — cette table ne fait que les lister dans cet ordre. Stable et reproductible : pour un
 * même N (nombre de compétences prises), les mêmes N post-it s'affichent toujours, jamais un
 * tirage recalculé.
 */
const RECT_POSTITS_LIEGE: Readonly<Record<string, readonly EmpriseObjet[]>> = {
  "sans-genre": [
    { x0: 27.344, y0: 28.472, x1: 29.395, y1: 33.333 },
    { x0: 29.004, y0: 25.521, x1: 32.91, y1: 35.417 },
    { x0: 25.293, y0: 31.076, x1: 27.051, y1: 35.243 },
    { x0: 31.25, y0: 31.424, x1: 32.91, y1: 34.722 },
    { x0: 21.484, y0: 32.639, x1: 25.293, y1: 38.368 },
    { x0: 25.391, y0: 34.375, x1: 27.344, y1: 39.062 },
    { x0: 30.566, y0: 34.201, x1: 32.617, y1: 39.236 },
    { x0: 26.758, y0: 32.118, x1: 29.59, y1: 42.361 },
    { x0: 21.68, y0: 37.153, x1: 23.633, y1: 41.667 },
    { x0: 24.023, y0: 36.806, x1: 25.781, y1: 41.146 },
    { x0: 28.809, y0: 35.938, x1: 30.762, y1: 40.799 },
    { x0: 24.707, y0: 39.583, x1: 26.855, y1: 44.965 },
    { x0: 21.582, y0: 40.799, x1: 24.707, y1: 46.354 },
  ],
  f: [
    { x0: 29.199, y0: 25.347, x1: 31.055, y1: 27.778 },
    { x0: 30.859, y0: 23.611, x1: 32.91, y1: 28.646 },
    { x0: 29.785, y0: 26.736, x1: 31.152, y1: 29.167 },
    { x0: 31.836, y0: 27.604, x1: 32.715, y1: 29.514 },
    { x0: 27.539, y0: 26.389, x1: 30.957, y1: 32.292 },
    { x0: 25.684, y0: 28.819, x1: 27.539, y1: 32.986 },
    { x0: 30.371, y0: 27.604, x1: 32.812, y1: 32.465 },
    { x0: 29.688, y0: 30.903, x1: 31.152, y1: 32.986 },
    { x0: 22.07, y0: 30.382, x1: 25.781, y1: 36.111 },
    { x0: 25.879, y0: 32.118, x1: 27.832, y1: 36.458 },
    { x0: 27.051, y0: 30.208, x1: 29.883, y1: 39.757 },
    { x0: 30.664, y0: 31.944, x1: 32.715, y1: 36.632 },
    { x0: 24.414, y0: 34.375, x1: 26.465, y1: 38.542 },
    { x0: 29.004, y0: 33.507, x1: 30.957, y1: 38.194 },
    { x0: 22.266, y0: 34.896, x1: 24.316, y1: 39.236 },
    { x0: 24.902, y0: 36.979, x1: 27.246, y1: 42.014 },
    { x0: 22.266, y0: 39.236, x1: 23.535, y1: 41.493 },
    { x0: 22.559, y0: 38.194, x1: 25.391, y1: 43.576 },
    { x0: 22.266, y0: 40.451, x1: 23.633, y1: 43.056 },
  ],
  h: [
    { x0: 29.102, y0: 27.257, x1: 31.055, y1: 31.771 },
    { x0: 29.98, y0: 28.299, x1: 30.859, y1: 30.208 },
    { x0: 27.441, y0: 27.604, x1: 29.688, y1: 33.333 },
    { x0: 29.59, y0: 25.521, x1: 32.812, y1: 35.417 },
    { x0: 25.391, y0: 31.076, x1: 27.539, y1: 35.243 },
    { x0: 21.387, y0: 32.639, x1: 25.488, y1: 38.194 },
    { x0: 27.148, y0: 32.292, x1: 29.59, y1: 38.021 },
    { x0: 25.586, y0: 33.854, x1: 27.539, y1: 38.889 },
    { x0: 30.664, y0: 34.201, x1: 33.301, y1: 39.41 },
    { x0: 21.875, y0: 37.326, x1: 23.926, y1: 41.667 },
    { x0: 24.121, y0: 36.806, x1: 25.977, y1: 41.146 },
    { x0: 28.613, y0: 35.764, x1: 30.859, y1: 41.146 },
    { x0: 26.855, y0: 37.153, x1: 29.102, y1: 42.708 },
    { x0: 24.902, y0: 39.583, x1: 26.953, y1: 44.792 },
    { x0: 21.777, y0: 40.625, x1: 25.195, y1: 46.354 },
    { x0: 26.562, y0: 41.667, x1: 27.539, y1: 43.576 },
  ],
};

/** La clé de table pour un genre — AU NOM DE FICHIER cette fois (`"sans-genre"`, pas `""`,
 *  `RECT_POSTITS_LIEGE`/les fichiers post-it suivent tous deux cette convention, contrairement à
 *  `cleRectGenre` qui sert `RECT_CALQUE_LIEGE`/`RECT_CALQUE_TENDANCES`). */
function cleFichierGenre(genre: GenreAvatar | null | undefined): string {
  return genre ?? "sans-genre";
}

/** Le nombre de post-it disponibles pour ce genre (13 sans genre, 19 pour f, 16 pour h — mesuré,
 *  voir `RECT_POSTITS_LIEGE`) : le plafond au-delà duquel `calquesLiegeChambreN1` n'affiche plus
 *  rien de plus, même si le joueur a pris davantage de compétences. */
export function nombrePostItsDisponibles(genre: GenreAvatar | null | undefined): number {
  return RECT_POSTITS_LIEGE[cleFichierGenre(genre)]!.length;
}

/**
 * ⟦C40 · A-163⟧ **Les post-it à afficher pour N compétences prises — fonction pure.** Zéro
 * compétence ⇒ tableau vide (le liège reste nu, l'appelant ne pose alors aucun calque, comme le
 * fond fixe le montre déjà). Le nombre affiché est le nombre de compétences prises, PLAFONNÉ au
 * nombre de post-it disponibles pour ce genre (`nombrePostItsDisponibles`) — jamais un index hors
 * tableau. Toujours les N PREMIERS de `RECT_POSTITS_LIEGE` (ordre de lecture fixe) : le même
 * post-it apparaît toujours pour le même N, jamais un tirage recalculé à chaque rendu. */
export function calquesLiegeChambreN1(genre: GenreAvatar | null | undefined, nombreCompetencesPrises: number): CalqueChambreN1[] {
  const cle = cleFichierGenre(genre);
  const rects = RECT_POSTITS_LIEGE[cle]!;
  const n = Math.min(Math.max(nombreCompetencesPrises, 0), rects.length);
  return rects.slice(0, n).map((rect, i) => ({
    chemin: `/scenes/chambre-n1-calque-liege-${cle}-${String(i + 1).padStart(2, "0")}.png`,
    rect,
  }));
}

/** Le calque « tableau des tendances » (à la place des deux cadres photo) — à poser seulement si
 *  `ouvert(state, "tendances")`, EXACTEMENT la condition qui rend l'objet `tendances` cliquable
 *  dans `Studio.tsx`. */
export function calqueTendancesChambreN1(genre: GenreAvatar | null | undefined): CalqueChambreN1 {
  return {
    chemin: `/scenes/chambre-n1-calque-tendances-${genre ?? "sans-genre"}.png`,
    rect: RECT_CALQUE_TENDANCES[cleRectGenre(genre)]!,
  };
}

/** Un calque `{chemin, rect}` depuis une des deux tables `RECT_CALQUE_POSE_*` — `null` si cette
 *  pose n'a pas d'entrée pour ce genre (`f`/`telephone`/`personnage`, le seul cas mesuré). */
function calqueDepuisTable(
  table: Readonly<Record<GenreAvatar, Readonly<Partial<Record<PoseAffichee, EmpriseObjet>>>>>,
  genre: GenreAvatar,
  pose: PoseAffichee,
  suffixe: "effacement" | "personnage",
): CalqueChambreN1 | null {
  const rect = table[genre][pose];
  if (!rect) return null;
  return { chemin: `/scenes/chambre-n1-calque-${genre}-${pose}-${suffixe}.png`, rect };
}

/**
 * ⟦C55 · A-172⟧ **Les objets de palier, calques pixel art posés DANS le dessin de `chambre-n1`.**
 * Sept objets (`DECORS_PALIER` + le cadre du premier virement, `Chambre.tsx`) : chacun est un PNG
 * RGBA rogné à sa boîte englobante, fabriqué en local (klein9b-edit, atelier Matt Studio, zéro
 * crédit) à partir du fond SANS genre puis découpé par masque de différence (scripts et essais :
 * `assets/making-of/2026-10-01-C55-chambre/`). Même convention que les calques de mur et de pose
 * (⟦C34⟧) : un fichier par genre (`f`, `h`, `sans-genre`), parce que les trois fonds n'ont pas
 * tout à fait le même cadrage (similitude mesurée par `matcher`, `scripts/_alignement_chambre_
 * commun.py` : h ×1,010, f ×1,059 par rapport au fond sans genre) : l'objet dessiné une fois est
 * recalé dans chaque cadre, jamais redessiné trois fois différemment.
 *
 * Rectangles en % de l'image 1024×576, ceux imprimés par `scripts/chambre-calques-paliers-c55.py`
 * (relançable) : s'ils bougent un jour, les retaper aux DEUX endroits. Aucun ne touche une zone
 * de pose de l'avatar, une emprise de meuble cliquable ni le liège/les tendances : verrouillé par
 * `chambre.test.ts` (« ⟦C55⟧ les sept calques de palier »), pas seulement mesuré une fois.
 *
 * ⟦C55 bis · A-175⟧ **Un huitième calque, le tableau d'honneur, et six plaques par-dessus** (mêmes fichiers, même
 * table de rectangles, même script : `scripts/chambre-calques-paliers-c55.py`, essais et esquisses dans
 * `assets/making-of/2026-10-02-C55b-plaques/`). Le tableau est au mur de droite, à droite de la fenêtre, dans la
 * bande étroite que laissent l'emprise cliquable de la fenêtre (cadre f : le fond f est décalé de 25 px vers la
 * gauche) et la bande de mur où l'avatar se pose au bureau (cadre h) : d'où un tableau de deux colonnes sur trois
 * rangées plutôt qu'un tableau large. **La lampe d'ambiance a changé de mur** pour lui laisser sa place : elle est
 * maintenant au mur de gauche, près du coin, au-dessus de la tête de l'avatar au repos (dessin retourné,
 * l'anneau néon étant symétrique). Aucun autre objet de C55 n'a bougé (les calques sont reproduits au pixel près).
 */
export type IdDecorPalier = "plante" | "fauteuil" | "fan-art" | "lampe" | "micro" | "mascotte" | "virement" | "tableau";

export const IDS_DECOR_PALIER: readonly IdDecorPalier[] = ["plante", "fauteuil", "fan-art", "lampe", "micro", "mascotte", "virement", "tableau"];

/**
 * ⟦C55 bis · A-175, décision de Matt du 02/10/2026⟧ **Le tableau d'honneur et ses plaques.**
 * « Pour la chambre, les médailles, ça doit être un tableau qui s'ajoute. » La rangée CSS d'emojis
 * 🥈/🥇 (`.plaques-mur`), posée hors de la pièce dans le bleu du fond, a disparu : un tableau
 * dessiné (`tableau`, le huitième calque de `IDS_DECOR_PALIER`) apparaît au mur de `chambre-n1` à
 * la première plaque (100 000 abonnés), vide, avec six emplacements ; chaque plaque gagnée s'y
 * ajoute ensuite en calque dessiné, à SA place (une case par palier, toujours la même : l'ordre
 * de gain et l'ordre des cases coïncident puisque les paliers ne se gagnent que dans l'ordre).
 *
 * `PALIERS_PLAQUE` = tous les paliers à plaque du moteur (`milestones_reached` ≥ 100 000, la même
 * règle que l'ancienne rangée) : 100 k, 500 k, 1 M, puis 2 M, 5 M, 10 M (mode « Après la rue »,
 * `PALIERS_POST_1M`). Six cases pour six plaques : aucune plaque n'est sans place. Si le moteur
 * gagne un jour un palier à plaque de plus, `chambre.test.ts` casse (la liste ci-dessous est
 * comparée à `PALIERS` + `PALIERS_POST_1M`) : il faut alors dessiner une case de plus, jamais
 * laisser une plaque sans calque (`calquePlaqueChambreN1` rend `null` pour un palier inconnu :
 * rien ne se pose, ni emoji ni texte). L'argent pour les paliers avant le million, l'or à partir
 * du million (la règle de l'ancienne rangée), dessinés dans le calque : le code ne choisit plus
 * un emoji.
 */
export const PALIERS_PLAQUE = [100_000, 500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000] as const;
export type PalierPlaque = (typeof PALIERS_PLAQUE)[number];
export type IdPlaque = `plaque-${PalierPlaque}`;

// @@C55-RECTS-DEBUT (écrit par scripts/chambre-calques-paliers-c55.py, ne pas retoucher à la main)
const RECT_CALQUE_DECO: Readonly<Record<IdDecorPalier | IdPlaque, Readonly<Record<string, EmpriseObjet>>>> = {
  "plante": {
    "": { x0: 44.238, y0: 75.694, x1: 55.176, y1: 95.833 },
    "f": { x0: 43.652, y0: 71.528, x1: 53.906, y1: 89.583 },
    "h": { x0: 44.141, y0: 75.521, x1: 54.590, y1: 95.139 },
  },
  "fauteuil": {
    "": { x0: 54.785, y0: 53.993, x1: 62.109, y1: 64.931 },
    "f": { x0: 53.516, y0: 50.868, x1: 60.156, y1: 61.111 },
    "h": { x0: 54.492, y0: 53.993, x1: 61.523, y1: 64.931 },
  },
  "fan-art": {
    "": { x0: 52.637, y0: 9.549, x1: 56.934, y1: 23.785 },
    "f": { x0: 51.465, y0: 8.681, x1: 55.566, y1: 22.222 },
    "h": { x0: 52.344, y0: 9.896, x1: 56.641, y1: 24.132 },
  },
  "lampe": {
    "": { x0: 44.727, y0: 9.896, x1: 50.098, y1: 23.958 },
    "f": { x0: 44.043, y0: 9.028, x1: 49.023, y1: 22.222 },
    "h": { x0: 44.629, y0: 10.417, x1: 49.805, y1: 24.132 },
  },
  "micro": {
    "": { x0: 45.215, y0: 46.701, x1: 50.781, y1: 75.000 },
    "f": { x0: 44.434, y0: 43.750, x1: 49.707, y1: 70.486 },
    "h": { x0: 45.020, y0: 46.701, x1: 50.586, y1: 74.826 },
  },
  "mascotte": {
    "": { x0: 55.371, y0: 65.972, x1: 66.113, y1: 86.806 },
    "f": { x0: 54.004, y0: 61.979, x1: 64.258, y1: 81.597 },
    "h": { x0: 55.078, y0: 65.799, x1: 65.723, y1: 86.458 },
  },
  "virement": {
    "": { x0: 54.590, y0: 25.868, x1: 58.496, y1: 38.889 },
    "f": { x0: 53.320, y0: 23.958, x1: 57.031, y1: 36.458 },
    "h": { x0: 54.297, y0: 26.042, x1: 58.203, y1: 39.062 },
  },
  "tableau": {
    "": { x0: 75.684, y0: 25.174, x1: 82.715, y1: 44.618 },
    "f": { x0: 73.242, y0: 23.438, x1: 79.883, y1: 41.840 },
    "h": { x0: 75.195, y0: 25.347, x1: 82.129, y1: 44.618 },
  },
  "plaque-100000": {
    "": { x0: 76.562, y0: 27.778, x1: 79.004, y1: 31.944 },
    "f": { x0: 74.023, y0: 25.868, x1: 76.465, y1: 29.861 },
    "h": { x0: 76.074, y0: 27.951, x1: 78.516, y1: 32.292 },
  },
  "plaque-500000": {
    "": { x0: 79.102, y0: 29.688, x1: 81.543, y1: 33.854 },
    "f": { x0: 76.465, y0: 27.604, x1: 78.809, y1: 31.771 },
    "h": { x0: 78.516, y0: 29.861, x1: 81.055, y1: 34.028 },
  },
  "plaque-1000000": {
    "": { x0: 76.660, y0: 31.250, x1: 78.906, y1: 35.243 },
    "f": { x0: 74.121, y0: 29.167, x1: 76.367, y1: 32.986 },
    "h": { x0: 76.172, y0: 31.424, x1: 78.418, y1: 35.417 },
  },
  "plaque-2000000": {
    "": { x0: 79.199, y0: 33.160, x1: 81.445, y1: 37.153 },
    "f": { x0: 76.562, y0: 30.903, x1: 78.711, y1: 34.896 },
    "h": { x0: 78.613, y0: 33.333, x1: 80.957, y1: 37.326 },
  },
  "plaque-5000000": {
    "": { x0: 76.660, y0: 34.722, x1: 78.906, y1: 38.715 },
    "f": { x0: 74.121, y0: 32.292, x1: 76.367, y1: 36.285 },
    "h": { x0: 76.172, y0: 34.896, x1: 78.418, y1: 38.889 },
  },
  "plaque-10000000": {
    "": { x0: 79.199, y0: 36.632, x1: 81.445, y1: 40.625 },
    "f": { x0: 76.562, y0: 34.201, x1: 78.711, y1: 38.021 },
    "h": { x0: 78.613, y0: 36.632, x1: 80.957, y1: 40.799 },
  },
};
// @@C55-RECTS-FIN

/** Le calque d'un objet de palier pour ce genre (ou sans genre) : `{chemin, rect}` — `rect` converti
 *  par `styleEmpriseHotspot`, le MÊME calcul que les hotspots et les autres calques, pour rester
 *  aligné au dessin quel que soit le recadrage (`cover`/`contain`/échelle mobile). */
export function calqueDecoChambreN1(id: IdDecorPalier, genre: GenreAvatar | null | undefined): CalqueChambreN1 {
  return {
    chemin: `/scenes/chambre-n1-calque-deco-${id}-${cleFichierGenre(genre)}.png`,
    rect: RECT_CALQUE_DECO[id][cleRectGenre(genre)]!,
  };
}

/** ⟦C55 bis · A-175⟧ Le calque de la plaque d'un palier (même convention de fichier et de rectangle
 *  que les objets de palier : un PNG par genre, rogné à sa boîte englobante, posé sur la case du
 *  tableau). `null` si ce palier n'a pas de case (`PALIERS_PLAQUE`) : rien ne se pose. */
export function calquePlaqueChambreN1(palier: number, genre: GenreAvatar | null | undefined): CalqueChambreN1 | null {
  if (!(PALIERS_PLAQUE as readonly number[]).includes(palier)) return null;
  const id = `plaque-${palier}` as IdPlaque;
  return {
    chemin: `/scenes/chambre-n1-calque-deco-${id}-${cleFichierGenre(genre)}.png`,
    rect: RECT_CALQUE_DECO[id][cleRectGenre(genre)]!,
  };
}

/**
 * ⟦C34 · A-159 · C40 bis · A-163 · C41 · A-164⟧ **Les deux calques (`effacement`, `personnage`)
 * pour cette pose** — `null` pour `debout` (le fond EST cette pose), `commode` (jamais
 * atteignable) ou genre absent (parties d'avant ⟦A-4 §2⟧ : pas de calque de pose, `Chambre.tsx`
 * retombe sur le sprite CSS superposé, comportement inchangé).
 *
 * **Pourquoi deux calques, pas un** (⟦C40 bis⟧, docs/chantiers/C40-le-mur-corrige.md point 2) :
 * l'ancien calque unique de ⟦C34⟧ était fabriqué par différence brute avec `debout` — il
 * contenait donc À LA FOIS la zone où le personnage DEBOUT du repos doit être effacé (repeint
 * avec le décor) ET le personnage à son objet. Translater ce calque ENTIER (tenté pour
 * `"tendances"` en ⟦C40⟧/⟦C40 bis⟧, voir le commentaire de `PoseAffichee`) déplaçait aussi
 * l'effacement : le personnage du repos réapparaissait au milieu de la pièce, et le calque en
 * repeignait un second ailleurs. Séparés une fois pour toutes dans `scripts/chambre-calques-
 * effacement-c40bis.py` : `effacement` ne bouge JAMAIS (posé à sa position native), `personnage`
 * non plus depuis ⟦C41⟧ (voir plus bas). Vérifié par recomposition
 * (`assets/making-of/2026-09-18-C40-mur/bis/rapport/rapport-effacement-personnage.json`) :
 * `effacement ∪ personnage`, posés à leur rectangle natif, reproduisent l'ancien calque unique à
 * ZÉRO écart pour les 10 combinaisons genre × pose — `lit`/`bureau`/`fenetre`/`tableau` (survol du
 * liège) ne changent donc pas d'un pixel après ce chantier.
 *
 * ⟦C41 · A-164⟧ **`"tendances"` a maintenant ses PROPRES calques**, mesurés par
 * `scripts/chambre-pose-tendances-c41.py` sur une planche DÉDIÉE (chambre en référence, jamais le
 * panneau de liège dans le cadre — voir le commentaire de `PoseAffichee`) : `calqueDepuisTable`
 * la traite comme n'importe quelle autre pose, plus de branche spéciale ni de translation. */
export function calquePoseChambreN1(genre: GenreAvatar | null | undefined, pose: PoseAffichee): CalquesPoseChambreN1 | null {
  if (!genre) return null;
  if (pose === "debout" || pose === "commode") return null;
  const effacement = calqueDepuisTable(RECT_CALQUE_POSE_EFFACEMENT, genre, pose, "effacement");
  const personnage = calqueDepuisTable(RECT_CALQUE_POSE_PERSONNAGE, genre, pose, "personnage");
  if (!effacement && !personnage) return null;
  return { effacement, personnage };
}

/** Cette scène se rend-elle en calques (fond fixe + calques, ⟦C34⟧) plutôt qu'en planche unique ?
 *  Sert à `Chambre.tsx` pour savoir si le décalage d'étiquette calibré contre l'AVATAR SUPERPOSÉ
 *  (`DECALAGE_ETIQUETTE`, mesuré ⟦C21 bis · point 2⟧ contre l'ancienne planche n1) a encore un
 *  sens : sur une scène en calques, l'avatar ne se superpose plus jamais (sauf `victoire` ou
 *  genre inconnu, deux cas où l'étiquette n'a de toute façon pas été mesurée à nouveau) — le
 *  décalage y déplacerait l'étiquette sans raison plutôt que d'éviter un chevauchement qui
 *  n'existe plus. Seule `chambre-n1` (jour et nuit, même fichier PNG) est couverte aujourd'hui. */
const SCENES_EN_CALQUES: ReadonlySet<string> = new Set(["chambre-n1-jour", "chambre-n1-nuit"]);
export function scenePossedePlanchesEtat(scene: string): boolean {
  return SCENES_EN_CALQUES.has(scene);
}

/**
 * ⟦C31⟧ **Retiré : l'ancre de la PLAQUE (nom + coût), qui migrait loin du hotspot de clic sur une
 * scène à planches d'état.** Vivait ici depuis ⟦C26 bis · point 1⟧ pour éloigner la plaque
 * VISIBLE du visage du personnage peint sur `lit`/`bureau` — depuis la décision de Matt du
 * 16/09 (docs/chantiers/C31-sans-etiquettes.md, avenant A-156 : « pas besoin des étiquettes [...]
 * vu que tu as déjà les boutons d'action en bas »), cette plaque ne montre plus rien du tout pour
 * `lit`/`bureau` (`Chambre.tsx`, `contenuVisible`), il n'y a donc plus rien à éloigner. Le
 * hotspot de clic, lui, ne bougeait déjà pas avec cette ancre (c'était tout son intérêt) : sa
 * position reste inchangée, portée par `empriseObjetChambre`/`styleEmpriseHotspot` uniquement.
 */

/** Un rectangle en pixels (repère écran, comme `getBoundingClientRect`). */
export interface RectanglePx {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

/**
 * ⟦C30 · lot E · point 3⟧ **Les étiquettes ne se posent plus sur le texte de Matt.** Le décalage
 * vertical (px, vers le HAUT, 0 si aucun) à appliquer à un objet dont le rectangle chevauche celui
 * de la boîte de Matt (`.boite-corps` : texte ET portraits) — jamais latéral, la boîte occupe
 * toujours toute la largeur du bas de la scène, un décalage horizontal ne dégagerait rien.
 *
 * `rectObjet` est le rectangle RENDU (reflète déjà `decalageActuel` s'il y en avait un au rendu
 * précédent) : cette fonction reconstitue d'abord le rectangle NATUREL (sans décalage, `+
 * decalageActuel`) pour calculer le besoin ENTIER à chaque appel, jamais un delta incrémental
 * (`Chambre.tsx`, l'effet qui mesure `decalagesBoite`) : sans ça, un objet déjà remonté ne
 * chevaucherait plus au prochain calcul (il mesurerait sa propre position déjà corrigée), son
 * décalage retomberait à 0, il redescendrait, chevaucherait de nouveau — un aller-retour sans fin
 * plutôt qu'un point fixe.
 */
export function decalageAntiChevauchementBoite(
  rectObjet: RectanglePx,
  rectBoite: RectanglePx,
  decalageActuel: number,
  marge = 8,
): number {
  const bas = rectObjet.bottom + decalageActuel;
  const haut = rectObjet.top - decalageActuel;
  const chevauche = bas > rectBoite.top && haut < rectBoite.bottom && rectObjet.right > rectBoite.left && rectObjet.left < rectBoite.right;
  if (!chevauche) return 0;
  return bas - rectBoite.top + marge;
}

/**
 * ⟦C30 · lot E · point 1⟧ **L'emprise RÉELLE de chaque objet peint dans le décor, en % de
 * l'image** — un rectangle (coin haut-gauche, coin bas-droit), jamais un point. Verbatim de Matt
 * (15/09) : « les zones où il faut avoir la souris [...] elles sont minuscules ». Avant ce point,
 * la zone de survol était un petit rectangle à taille FIXE (`--taille`/padding CSS) CENTRÉ sur le
 * hotspot de clic (`HOTSPOTS_PAR_SCENE`, souvent un point précis DANS le meuble — l'oreiller du
 * lit, l'écran du bureau — pas son centre géométrique) : sur un lit qui occupe le quart de
 * l'image, quelques dizaines de pixels de marge autour d'un point ne couvrent qu'une fraction du
 * meuble dessiné.
 *
 * Relevé À LA MAIN sur la planche de repos `chambre-n1-jour.png` (1024×576, la même composition
 * que les planches d'état, seul le personnage diffère), grille de repérage en % (jamais deviné,
 * ⟦A-137⟧) : le lit (cadre + literie, sans déborder sur le tapis), le panneau à post-it SEUL (les
 * deux cadres-photo voisins n'ont pas de hotspot, ⟦A-137⟧ interdit de leur en inventer un), la
 * fenêtre ET son rideau (l'ensemble accroché au mur, pas la seule vitre), le bureau complet
 * (moniteur, clavier, chaise, caisson — l'ensemble du poste de travail, pas seulement l'écran que
 * vise `POSITION_ECRAN_PAR_SCENE`). Aucun chevauchement entre ces quatre rectangles mesurés (le
 * plus proche : bas du panneau à 50,7 % ⟦tour 3⟧, haut du lit à 52 % ⟦tour 2⟧, 1,3 point d'écart) :
 * aucun arbitrage de priorité nécessaire pour ces quatre-là. `telephone` n'a AUCUN objet dessiné sur cette planche
 * (sa position, x:31/y:66 dans `Studio.tsx`, est un repère générique hérité de l'ancien décor
 * doodle) : rien à mesurer sur le dessin, donc pas d'entrée ici — son hotspot garde sa taille par
 * défaut (`.objet-cliquable`), non couverte par ce chantier (signalé au rapport).
 *
 * ⟦tour 3⟧ **`tableau` recorrigé : le cadre a une ÉPAISSEUR, deux coins en débordent.** La
 * vérification du tour 2 (grille de repérage en %, `assets/making-of/2026-09-15-C30-E-zones/
 * verif-tour2/point1/resultats-trou-tableau.json` + `crop-tab-coinHaut.png`/`crop-tab-coinBas.png`)
 * a trouvé un point du panneau (31, 25) hors de l'emprise `y0: 27` alors qu'il est clairement sur
 * le panneau. Reprise à la grille fine (pas de 0,5 %, `assets/making-of/
 * 2026-09-15-C30-E-zones/tour3/v3-coin-haut-fin.png` et `v3-coin-bas-fin.png`) : le panneau est un
 * cadre en isométrie AVEC ÉPAISSEUR — chaque coin porte un petit « capuchon » (la tranche du cadre)
 * qui dépasse la diagonale principale du bord. Coin haut-droit : le capuchon culmine à (33, 23,0),
 * pas (31, 27) — l'ancienne mesure ne portait que sur le bord diagonal du cadre, pas sur son
 * capuchon, qui masque à lui seul un post-it entier. Coin bas-gauche : le capuchon symétrique
 * descend jusqu'à (21, 50,3), pas 48. Nouvelle emprise avec marge (mêmes marges que les trois
 * autres objets, jamais pile sur le pixel) : `x0` 20,5 → 19,8 (bord gauche du cadre relevé à 20,2 à
 * la grille fine), `y0` 27 → 22,3 (capuchon haut-droit à 23,0), `x1` 34 → 34,6 (capuchon haut-droit
 * à 34,1, le cadre-photo voisin commence à 34,9 : la marge ne le touche pas), `y1` 48 → 50,7
 * (capuchon bas-gauche à 50,3). Toujours aucun chevauchement avec les trois autres rectangles
 * (vérifié à nouveau, `chambre.test.ts`) : le plus proche redevient le lit, 1,3 point d'écart au
 * lieu de 4 — resserré mais jamais nul.
 *
 * `chambre-n1-nuit` est le MÊME fichier PNG que `chambre-n1-jour` (`md5` identique, comme
 * `COULEUR_COIN_PAR_SCENE`) : même table pour les deux clés.
 *
 * ⟦tour 4⟧ **Lit et bureau recorrigés : Matt (15/09, après avoir joué) « je voudrais que tout le
 * lit et tout le bureau soit la zone [...] Là c'est juste une enceinte sur le bureau ou le bas du
 * coussin. »** Le tour 2 avait déjà relevé un écart : son vérificateur indépendant mesurait le lit
 * dessiné à `{ x0: 17.3, y0: 52.0, x1: 43.0, y1: 83.0 }` contre une emprise codée alors arrêtée à
 * `x1: 42, y1: 81` — 88 à 91 % de couverture seulement (aires), jamais corrigé depuis. Reprise
 * ENTIÈRE au tour 4, grille de repérage en % au pas de 0,5 point, zoomée, jamais deviné, partie par
 * partie (`assets/making-of/2026-09-15-C30-E-zones/tour4/mesure/`, crops `bord-lit-*.png` et
 * `bord-bureau-*.png`) :
 * - **lit** : tête de lit (poteau arrondi le plus haut) culmine à (24.8, 51.7) — cohérent avec le
 *   y0=52 du tour 2, marge conservée. Le pied AVANT (le plus proche du tapis, la jambe du cadre qui
 *   touche le sol) descend à (32.2, 81.8) : `y1` codé à 81 le rognait de 0,8 point. Le pied côté
 *   tapis (coin cadre + matelas) atteint (41.3, 77.5) en x, cohérent avec la mesure indépendante du
 *   tour 2 (43,0) une fois sa propre marge retirée. Montant gauche de tête de lit à x=17,0. Nouvelle
 *   emprise, petite marge : `x0` 17→16.5, `y0` 52→51.3 (garde 0,6 point d'écart avec le bas du
 *   tableau, 50.7, toujours aucun chevauchement), `x1` 42→43.4, `y1` 81→83.3.
 * - **bureau** : le vrai point haut n'est PAS l'enceinte (celle que Matt voyait survolable) mais le
 *   coin haut-droit du MONITEUR, à (78.3, 48.0) — 3 points au-dessus de l'ancien `y0: 51`, exactement
 *   ce que Matt signalait. Bord gauche : le flanc du caisson du bureau (pas la chaise, qui est
 *   légèrement plus à droite) à x=67.6. Bord droit : le flanc du caisson à tiroirs, x=83.7 (l'enceinte
 *   et la tour, posées SUR le bureau entre x=79 et x=83, restent dans ce rectangle). Bas RÉEL : les
 *   roulettes de la chaise (l'objet le plus bas du poste, pas le sol vide que l'ancien `y1: 79`
 *   incluait en trop) touchent le sol à (69.3, 73.3) et (73.8, 72.8). Nouvelle emprise, petite marge :
 *   `x0` 68→67.0, `y0` 51→47.3, `x1` 83→84.4, `y1` 79→74.0 (resserré sur l'objet réel : l'ancien
 *   79 dépassait de 5,7 points dans le vide, sans y avoir jamais manqué de couverture).
 * - **fenêtre** : revérifiée au même pas fin, cadre + tringle + rideaux au complet (aucun appui
 *   dessiné sous le cadre). Poignée haute de la tringle à (61.2, 16.3), coin bas-droit du cadre à
 *   (72.2, 43.2) : l'ancienne emprise (60,17,72,43) couvrait déjà quasiment tout, resserrée à peine
 *   ailleurs. Nouvelle emprise, petite marge : `x0` 60→60.5, `y0` 17→15.6, `x1` 72→73.1, `y1`
 *   43→44.6.
 *
 * Aucun chevauchement entre les quatre rectangles (revérifié, `chambre.test.ts`) : le plus proche
 * reste lit/tableau, 0,6 point (51.3 contre 50.7) — resserré depuis le tour 3 (1,3 point) parce que
 * le lit grandit vers le haut, mais jamais nul, donc aucun arbitrage de priorité nécessaire.
 */
export interface EmpriseObjet {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}
const EMPRISE_PAR_SCENE: Readonly<Record<string, Readonly<Record<string, EmpriseObjet>>>> = {
  "chambre-n1-jour": {
    // ⟦tour 2⟧ `y0` corrigé de 57 à 52 : vérification contradictoire (grille de repérage en %,
    // `assets/making-of/2026-09-15-C30-E-zones/point1/lit-grille-xy.png`), le haut du montant de
    // tête de lit (le poteau arrondi) culmine à ~52 %, pas 57 : l'emprise ne couvrait donc pas les
    // 5 premiers points de % du montant. `x0` (17) restait déjà généreux (le montant démarre vers
    // 21 %, marge conservée) : seul `y0` bougeait.
    // ⟦tour 4⟧ tout l'objet dessiné, pas une fraction — voir le commentaire de `EMPRISE_PAR_SCENE`
    // plus haut pour le détail des mesures (grille en %, pas de 0,5 point) et des marges.
    lit: { x0: 16.5, y0: 51.3, x1: 43.4, y1: 83.3 },
    bureau: { x0: 67.0, y0: 47.3, x1: 84.4, y1: 74.0 },
    fenetre: { x0: 60.5, y0: 15.6, x1: 73.1, y1: 44.6 },
    // ⟦tour 3⟧ le cadre a une épaisseur : ses deux coins (haut-droit, bas-gauche) débordaient de
    // l'ancienne emprise (20.5, 27, 34, 48), voir le commentaire de `EMPRISE_PAR_SCENE` plus haut.
    tableau: { x0: 19.8, y0: 22.3, x1: 34.6, y1: 50.7 },
    // ⟦C33 · A-158⟧ Le tableau des tendances, peint à la place des deux anciens cadres-photo,
    // à la place mesurée sur l'image FINALE (après branchement, jamais devinée sur l'édition
    // brute) : `FRAMES_RECT` de `scripts/mur-veille-branchement.py` (354, 110, 444, 223 px sur
    // 1024×576), reconverti en % — le même rectangle qui a servi au collage, donc garanti
    // aligné sur ce qui est réellement dessiné. Touche `tableau` par la tranche (x1 = 34.6 =
    // tendances.x0), jamais un chevauchement (`chambre.test.ts`, le même test générique que les
    // quatre autres objets).
    tendances: { x0: 34.6, y0: 19.1, x1: 43.4, y1: 38.7 },
  },
  "chambre-n1-nuit": {
    lit: { x0: 16.5, y0: 51.3, x1: 43.4, y1: 83.3 },
    bureau: { x0: 67.0, y0: 47.3, x1: 84.4, y1: 74.0 },
    fenetre: { x0: 60.5, y0: 15.6, x1: 73.1, y1: 44.6 },
    tableau: { x0: 19.8, y0: 22.3, x1: 34.6, y1: 50.7 },
    tendances: { x0: 34.6, y0: 19.1, x1: 43.4, y1: 38.7 },
  },
};

/** L'emprise mesurée de `(scene, id)`, ou `null` si aucune n'a été relevée pour cet objet (garde
 *  alors la taille par défaut du hotspot, comportement d'avant ce point). */
export function empriseObjetChambre(scene: string, id: string): EmpriseObjet | null {
  return EMPRISE_PAR_SCENE[scene]?.[id] ?? null;
}

/**
 * ⟦C30 · lot E · point 1⟧ Le rectangle CSS (position % de boîte + taille px) d'un hotspot
 * agrandi à l'emprise mesurée : centré sur le CENTRE du rectangle `emprise` (jamais l'ancre de
 * clic, qui n'est souvent pas au centre géométrique du meuble — « pile sur l'oreiller » pour le
 * lit), reconverti par `positionDansBoite`/`rectangleImageAffichee` comme tout le reste de ce
 * module : correct à n'importe quelle taille d'écran, `cover` (ordinateur) ou `contain`/échelle
 * explicite (mobile), sans un second calcul susceptible de diverger.
 */
export function styleEmpriseHotspot(
  emprise: EmpriseObjet,
  boite: { largeur: number; hauteur: number },
  mode: AjustementScene,
  ancrageVertical: AncrageVertical,
): { left: number; top: number; largeurPx: number; hauteurPx: number } {
  const centre = { x: (emprise.x0 + emprise.x1) / 2, y: (emprise.y0 + emprise.y1) / 2 };
  const position = positionDansBoite(centre, boite, mode, ancrageVertical);
  const rectImage = rectangleImageAffichee(boite, mode);
  return {
    left: position.x,
    top: position.y,
    largeurPx: ((emprise.x1 - emprise.x0) / 100) * rectImage.largeur,
    hauteurPx: ((emprise.y1 - emprise.y0) / 100) * rectImage.hauteur,
  };
}
